<template>
  <div class="admin-container">
    <!-- Sidebar -->
    <AdminSidebar :isOpen="isSidebarOpen" :userData="currentUser" />

    <!-- Main Content -->
    <div class="main-content" :class="{ 'content-shifted': isSidebarOpen }">
      <nav class="admin-nav">
        <button class="menu-btn" @click="toggleSidebar">
          <i class="fas fa-bars"></i>
        </button>
      </nav>

      <div class="admin-content">
        <div class="data-section">
          <div class="section-header">
            <h2><i class="fas fa-bus-alt"></i> Quản Lý Chuyến Đi</h2>
            <div class="action-buttons">
              <button class="add-btn" @click="openAddModal">
                <i class="fas fa-plus"></i> Thêm Chuyến Đi
              </button>
              <div class="search-box">
                <i class="fas fa-search"></i>
                <input
                  type="text"
                  v-model="searchQuery"
                  placeholder="Tìm theo tuyến đường hoặc tài xế..."
                  @keyup.enter="applySearch"
                />
                <button class="search-btn" @click="applySearch">
                  <i class="fas fa-search"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Filter accordion -->
          <div class="filter-accordion">
            <div class="filter-header" @click="toggleFilters">
              <div class="filter-title">
                <i class="fas fa-filter"></i>
                <span>Bộ Lọc</span>
                <div class="filter-badge" v-if="activeFilterCount > 0">
                  {{ activeFilterCount }}
                </div>
              </div>
              <i
                :class="
                  showFilters ? 'fas fa-chevron-up' : 'fas fa-chevron-down'
                "
              ></i>
            </div>
            <div class="filter-body" :class="{ 'filter-open': showFilters }">
              <div class="filter-inputs">
                <div class="filter-group">
                  <label><i class="fas fa-route"></i> Tuyến Đường:</label>
                  <select v-model="filterRouteId" class="filter-select">
                    <option value="">Tất cả tuyến đường</option>
                    <option
                      v-for="route in routes"
                      :key="route.route_id"
                      :value="route.route_id"
                    >
                      {{ route.destination_location }}
                    </option>
                  </select>
                </div>
                <div class="filter-group">
                  <label><i class="fas fa-bus"></i> Loại Xe:</label>
                  <select v-model="filterVehicleType" class="filter-select">
                    <option value="">Tất cả loại xe</option>
                    <option value="16_slots">Xe 16 chỗ</option>
                    <option value="28_slots">Xe 28 chỗ</option>
                    <option value="45_slots">Xe 45 chỗ</option>
                    <option value="over_night">Xe giường nằm</option>
                  </select>
                </div>
                <div class="filter-group">
                  <label><i class="fas fa-calendar-alt"></i> Từ:</label>
                  <div class="date-input-container">
                    <input
                      type="date"
                      v-model="dateRange.from"
                      @change="handleDateChange('from', $event)"
                    />
                  </div>
                </div>
                <div class="filter-group">
                  <label><i class="fas fa-calendar-alt"></i> Đến:</label>
                  <div class="date-input-container">
                    <input
                      type="date"
                      v-model="dateRange.to"
                      @change="handleDateChange('to', $event)"
                    />
                  </div>
                </div>
                <div class="filter-group">
                  <label><i class="fas fa-tasks"></i> Trạng Thái:</label>
                  <select v-model="filterStatus" class="filter-select">
                    <option value="">Tất cả trạng thái</option>
                    <option value="scheduled">Đã lên lịch</option>
                    <option value="in_progress">Đang chạy</option>
                    <option value="completed">Đã hoàn thành</option>
                    <option value="canceled">Đã hủy</option>
                  </select>
                </div>
                <div class="filter-buttons">
                  <button class="apply-filter-btn" @click="fetchTrips">
                    <i class="fas fa-check"></i> Áp dụng
                  </button>
                  <button class="reset-filter-btn" @click="resetFilters">
                    <i class="fas fa-redo"></i> Đặt lại
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading indicator -->
          <div v-if="isLoading" class="loading-container">
            <div class="loading-spinner">
              <div class="spinner"><span></span></div>
            </div>
            <p>Đang tải dữ liệu...</p>
          </div>

          <!-- Trips Table for Desktop -->
          <div
            v-else-if="!isLoading && filteredTrips.length > 0"
            class="table-responsive desktop-only"
          >
            <table>
              <thead>
                <tr>
                  <th>Mã</th>
                  <th>Tên Chuyến</th>
                  <th>Tuyến Đường</th>
                  <th>Thời gian khởi hành</th>
                  <th>Xe</th>
                  <th>Tài xế</th>
                  <th>Số chỗ đã đặt</th>
                  <th>Trạng thái</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="trip in filteredTrips" :key="trip.trip_id">
                  <td class="trip-id">#{{ trip.trip_id }}</td>
                  <td class="name">{{ trip.name || '' }}</td>
                  <td class="destination">
                    {{ trip.route?.destination_location || 'N/A' }}
                  </td>
                  <td class="departure-time">
                    {{ formatDateTime(trip.departure_datetime) }}
                  </td>
                  <td class="vehicle">
                    {{ formatVehicleType(trip.vehicle?.type) }}
                    <span class="license-plate">{{
                      trip.vehicle?.license_plate || 'N/A'
                    }}</span>
                  </td>
                  <td class="driver">
                    {{ trip.vehicle?.driver_name || 'N/A' }}
                  </td>
                  <td class="bookings-count">
                    <div class="booking-info">
                      <span
                        >{{ this.getRegularBookingsCount(trip) }}/{{
                          getVehicleCapacity(trip.vehicle?.type)
                        }}</span
                      >
                      <div class="progress-bar">
                        <div
                          class="progress"
                          :style="{
                            width: `${calculateBookingPercentage(trip)}%`
                          }"
                          :class="getBookingFillClass(trip)"
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td class="status">
                    <span
                      class="status-badge"
                      :class="trip.status?.toLowerCase()"
                    >
                      {{ translateTripStatus(trip.status) }}
                    </span>
                  </td>
                  <td class="actions">
                    <button
                      @click="viewTripDetails(trip)"
                      class="action-btn view-btn"
                      title="Xem chi tiết"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                    <button
                      @click="openEditModal(trip)"
                      class="action-btn edit-btn"
                      title="Chỉnh sửa"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button
                      @click="exportTripToExcel(trip)"
                      class="action-btn excel-btn"
                      title="Xuất Excel"
                    >
                      <i class="fas fa-file-excel"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Trips Cards for Mobile -->
          <div
            v-else-if="!isLoading && filteredTrips.length > 0"
            class="trip-cards mobile-only"
          >
            <div
              class="trip-card"
              v-for="trip in filteredTrips"
              :key="trip.trip_id"
            >
              <div class="trip-card-header">
                <div class="trip-id">#{{ trip.trip_id }}</div>
                <div class="trip-name" v-if="trip.name">{{ trip.name }}</div>
                <div class="status">
                  <span
                    class="status-badge"
                    :class="trip.status?.toLowerCase()"
                  >
                    {{ translateTripStatus(trip.status) }}
                  </span>
                </div>
              </div>
              <div class="trip-card-body">
                <div class="info-row">
                  <div class="info-label">
                    <i class="fas fa-map-marker-alt"></i> Tuyến:
                  </div>
                  <div class="info-value destination">
                    {{ trip.route?.destination_location || 'N/A' }}
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-label">
                    <i class="far fa-clock"></i> Khởi hành:
                  </div>
                  <div class="info-value">
                    {{ formatDateTime(trip.departure_datetime) }}
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-label"><i class="fas fa-bus"></i> Xe:</div>
                  <div class="info-value">
                    {{ formatVehicleType(trip.vehicle?.type) }}
                    <div class="small-text">
                      {{ trip.vehicle?.license_plate || 'N/A' }}
                    </div>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-label">
                    <i class="fas fa-user"></i> Tài xế:
                  </div>
                  <div class="info-value">
                    {{ trip.vehicle?.driver_name || 'N/A' }}
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-label">
                    <i class="fas fa-users"></i> Đặt chỗ:
                  </div>
                  <div class="info-value">
                    <div class="bookings-info">
                      <span class="booking-count">
                        {{ this.getRegularBookingsCount(trip) }}/{{
                          getVehicleCapacity(trip.vehicle?.type)
                        }}
                      </span>
                      <div class="progress-bar">
                        <div
                          class="progress"
                          :style="{
                            width: `${calculateBookingPercentage(trip)}%`
                          }"
                          :class="getBookingFillClass(trip)"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="trip-card-footer">
                <button
                  @click="viewTripDetails(trip)"
                  class="card-action-btn view-btn"
                >
                  <i class="fas fa-eye"></i> Chi tiết
                </button>
                <button
                  @click="openEditModal(trip)"
                  class="card-action-btn edit-btn"
                >
                  <i class="fas fa-edit"></i> Sửa
                </button>
                <button
                  @click="exportTripToExcel(trip)"
                  class="card-action-btn excel-btn"
                >
                  <i class="fas fa-file-excel"></i> Excel
                </button>
              </div>
            </div>
          </div>

          <!-- No data state -->
          <div v-else-if="!isLoading" class="no-data-container">
            <div class="empty-state">
              <div class="empty-icon">
                <i class="fas fa-bus"></i>
              </div>
              <h3>Không có dữ liệu chuyến đi</h3>
              <p>
                Chưa có chuyến đi nào được thêm vào hoặc không có kết quả phù
                hợp với bộ lọc.
              </p>
              <button class="refresh-btn" @click="fetchTrips">
                <i class="fas fa-sync-alt"></i> Tải lại dữ liệu
              </button>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination" v-if="!isLoading && filteredTrips.length > 0">
            <button
              class="pagination-btn"
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >
              <i class="fas fa-chevron-left"></i> Trước
            </button>

            <span class="page-info">
              Trang {{ currentPage }} / {{ totalPages }}
            </span>

            <button
              class="pagination-btn"
              :disabled="
                currentPage === totalPages || filteredTrips.length === 0
              "
              @click="changePage(currentPage + 1)"
            >
              Sau <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Trip Details Modal -->
    <div class="modal" v-if="selectedTrip" @click.self="closeDetailsModal">
      <div class="modal-content details-modal">
        <!-- ── Header ── -->
        <div class="dm-header">
          <div class="dm-header-left">
            <span class="dm-trip-id">#{{ selectedTrip.trip_id }}</span>
            <span class="dm-trip-name" v-if="selectedTrip.name">{{ selectedTrip.name }}</span>
            <span class="dm-route">{{ selectedTrip.route?.destination_location }}</span>
            <span class="dm-status-badge" :class="selectedTrip.status">{{ translateTripStatus(selectedTrip.status) }}</span>
          </div>
          <div class="dm-header-right">
            <button class="dm-edit-btn" @click="openEditModal(selectedTrip)"><i class="fas fa-edit"></i> Chỉnh sửa</button>
            <button class="dm-close-btn" @click="closeDetailsModal">×</button>
          </div>
        </div>

        <div class="dm-body">
          <div v-if="isLoadingDetails" class="dm-loading"><div class="loading-spinner"></div><span>Đang tải...</span></div>

          <div v-else class="dm-layout">
            <!-- ── LEFT: Info + Tables ── -->
            <div class="dm-left">

              <!-- Meta strip -->
              <div class="dm-meta-strip">
                <div class="dm-meta-item">
                  <i class="far fa-clock"></i>
                  <div>
                    <div class="dm-meta-label">Khởi hành</div>
                    <div class="dm-meta-val">{{ formatDateTime(selectedTrip.departure_datetime) }}</div>
                  </div>
                </div>
                <div class="dm-meta-item">
                  <i class="fas fa-bus"></i>
                  <div>
                    <div class="dm-meta-label">Phương tiện</div>
                    <div class="dm-meta-val">{{ formatVehicleType(selectedTrip.vehicle?.type) }}</div>
                  </div>
                </div>
                <div class="dm-meta-item">
                  <i class="fas fa-id-card"></i>
                  <div>
                    <div class="dm-meta-label">Biển số</div>
                    <div class="dm-meta-val mono">{{ selectedTrip.vehicle?.license_plate || 'N/A' }}</div>
                  </div>
                </div>
                <div class="dm-meta-item">
                  <i class="fas fa-user"></i>
                  <div>
                    <div class="dm-meta-label">Tài xế</div>
                    <div class="dm-meta-val">{{ selectedTrip.vehicle?.driver_name || 'N/A' }}</div>
                  </div>
                </div>
                <div class="dm-meta-item">
                  <i class="fas fa-phone-alt"></i>
                  <div>
                    <div class="dm-meta-label">SĐT tài xế</div>
                    <div class="dm-meta-val blue">{{ selectedTrip.vehicle?.driver_phone || 'N/A' }}</div>
                  </div>
                </div>
                <div class="dm-meta-item">
                  <i class="fas fa-tag"></i>
                  <div>
                    <div class="dm-meta-label">Giá cơ bản</div>
                    <div class="dm-meta-val green">{{ formatCurrency(selectedTrip.route?.price) }}</div>
                  </div>
                </div>
              </div>

              <!-- Loại vé -->
              <div class="dm-section">
                <div class="dm-section-header">
                  <span><i class="fas fa-ticket-alt"></i> Loại vé ({{ tripTicketTypes.length }})</span>
                  <button class="dm-add-btn" @click="openAddTicketTypeModal"><i class="fas fa-plus"></i> Thêm</button>
                </div>
                <table class="dm-table" v-if="tripTicketTypes.length">
                  <thead><tr><th>Loại</th><th>Mô tả</th><th>Giá</th><th></th></tr></thead>
                  <tbody>
                    <tr v-for="ticket in tripTicketTypes" :key="ticket.ticket_type_id">
                      <td>
                        {{ ticket.type }}
                        <span v-if="ticket.independent" class="dm-tag special">Đặc biệt</span>
                        <span v-if="ticket.is_hidden" class="dm-tag hidden">Ẩn</span>
                      </td>
                      <td class="text-muted">{{ ticket.description || '—' }}</td>
                      <td class="price-cell">{{ formatCurrency(ticket.price) }}</td>
                      <td class="actions-cell">
                        <button @click="openEditTicketTypeModal(ticket)" class="dm-icon-btn edit" title="Sửa"><i class="fas fa-pen"></i></button>
                        <button v-if="!ticket.is_hidden" @click="confirmDeleteTicketType(ticket)" class="dm-icon-btn hide" title="Ẩn"><i class="fas fa-eye-slash"></i></button>
                        <button v-if="ticket.is_hidden" @click="unhideTicketType(ticket)" class="dm-icon-btn show" title="Hiện"><i class="fas fa-eye"></i></button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="dm-empty"><i class="fas fa-ticket-alt"></i> Chưa có loại vé</div>
              </div>

              <!-- Điểm đón -->
              <div class="dm-section">
                <div class="dm-section-header">
                  <span><i class="fas fa-map-marker-alt"></i> Điểm đón ({{ tripPickupLocations.length }})</span>
                  <button class="dm-add-btn" @click="openAddPickupLocationModal"><i class="fas fa-plus"></i> Thêm</button>
                </div>
                <div class="dm-pickup-grid" v-if="tripPickupLocations.length">
                  <div v-for="loc in tripPickupLocations" :key="loc.pickup_location_id" class="dm-pickup-card">
                    <div class="dm-pickup-info">
                      <div class="dm-pickup-name">{{ loc.location_name }}</div>
                      <div class="dm-pickup-addr text-muted">{{ loc.address || 'Không có địa chỉ' }}</div>
                      <div class="dm-pickup-time" v-if="loc.pickup_time"><i class="far fa-clock"></i> {{ formatPickupTime(loc.pickup_time) }}</div>
                    </div>
                    <div class="dm-pickup-actions">
                      <button @click="openEditPickupLocationModal(loc)" class="dm-icon-btn edit" title="Sửa"><i class="fas fa-pen"></i></button>
                      <button @click="confirmDeletePickupLocation(loc)" class="dm-icon-btn delete" title="Xóa"><i class="fas fa-trash-alt"></i></button>
                    </div>
                  </div>
                </div>
                <div v-else class="dm-empty"><i class="fas fa-map-signs"></i> Chưa có điểm đón</div>
              </div>

              <!-- Hành khách -->
              <div class="dm-section">
                <div class="dm-section-header">
                  <span><i class="fas fa-users"></i> Hành khách ({{ tripBookings.length }})</span>
                  <div class="dm-legend" v-if="hasGroupBookings">
                    <span v-for="(color, idx) in groupColors" :key="color" class="dm-legend-dot" :style="{background: color}" :title="'Nhóm '+(idx+1)"></span>
                    <span class="text-muted" style="font-size:11px">Màu = cùng nhóm</span>
                  </div>
                </div>
                <div class="dm-table-wrap" v-if="tripBookings.length">
                  <table class="dm-table dm-passenger-table">
                    <thead>
                      <tr>
                        <th>Ghế</th><th>Họ tên</th><th>GT/NS</th><th>CCCD / Địa chỉ</th><th>SĐT / Email</th><th>Loại vé</th><th>Điểm đón</th><th>TT</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="b in tripBookings" :key="b.booking_id" :style="getBookingGroupStyle(b)">
                        <td class="seat-cell">{{ b.slot_number }}</td>
                        <td>
                          <div class="dm-name">{{ b.customer_name }}</div>
                          <div v-if="b.booking_group_id" class="dm-tag group"><i class="fas fa-users"></i> Nhóm</div>
                        </td>
                        <td>
                          <span v-if="b.gender" :class="['dm-gender', b.gender==='Nam'?'male':'female']">{{ b.gender }}</span>
                          <div class="text-muted small-txt">{{ formatDob(b.birth_date || b.birthDate, b.birth_year || b.birthYear, b.cccd) }}</div>
                        </td>
                        <td>
                          <div class="mono small-txt">{{ b.cccd || '—' }}</div>
                          <div class="text-muted small-txt cccd-addr">{{ b.cccd_address || '' }}</div>
                        </td>
                        <td>
                          <div class="small-txt">{{ b.customer_phone }}</div>
                          <div class="text-muted small-txt">{{ b.customer_email || '—' }}</div>
                        </td>
                        <td>
                          <div class="small-txt">{{ getTicketTypeName(b.ticket_type_id) }}</div>
                          <div class="price-text small-txt">{{ formatCurrency(getTicketTypePrice(b.ticket_type_id)) }}</div>
                          <div v-if="b.discount_code" class="dm-tag discount">{{ getDiscountDisplayText(b) }}</div>
                        </td>
                        <td class="small-txt text-muted">{{ getPickupLocationName(b.pickup_location_id) }}</td>
                        <td><span class="status-badge small" :class="b.status.toLowerCase()">{{ translateBookingStatus(b.status) }}</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="dm-empty"><i class="fas fa-users-slash"></i> Chưa có hành khách</div>
              </div>
            </div>

            <!-- ── RIGHT: Seat map ── -->
            <div class="dm-right">
              <div class="dm-seat-panel">
                <div class="dm-seat-header">
                  <span><i class="fas fa-th-large"></i> Sơ đồ ghế</span>
                  <div class="dm-seat-btns">
                    <template v-if="!isSeatEditMode">
                      <button @click="enterSeatEditMode" class="dm-sm-btn lock" title="Khóa ghế thủ công"><i class="fas fa-lock"></i></button>
                      <button @click="fetchSeatMap" class="dm-sm-btn refresh" :disabled="isLoadingSeatMap" title="Làm mới">
                        <i class="fas fa-sync-alt" :class="{'fa-spin': isLoadingSeatMap}"></i>
                      </button>
                    </template>
                    <template v-else>
                      <button @click="saveLockedSeats" :disabled="isSavingLockedSeats" class="dm-sm-btn save">
                        <i class="fas fa-spinner fa-spin" v-if="isSavingLockedSeats"></i>
                        <i class="fas fa-save" v-else></i> Lưu
                      </button>
                      <button @click="cancelSeatEdit" class="dm-sm-btn cancel">Hủy</button>
                    </template>
                  </div>
                </div>

                <div v-if="isSeatEditMode" class="dm-seat-notice">
                  <i class="fas fa-info-circle"></i> Click ghế <strong>trống</strong> để khoá · Click ghế <span class="dm-locked-hint"><i class="fas fa-lock"></i> đang khoá</span> để mở khoá
                </div>

                <div v-if="isLoadingSeatMap && !seatMapData" class="dm-empty"><i class="fas fa-spinner fa-spin"></i> Đang tải...</div>

                <div v-if="seatMapData" class="dm-seat-body">
                  <AdminSeatLayout
                    :vehicle-type="seatMapData.vehicle_type"
                    :booked-seats="seatMapData.booked_seats"
                    :held-seats="seatMapData.held_seats"
                    :locked-seats="isSeatEditMode ? editingLockedSeats : seatMapData.locked_seats"
                    :selected-seats="[]"
                    @seat-selected="toggleSeatLock"
                  />
                  <div class="dm-seat-time" v-if="seatMapLastUpdated && !isSeatEditMode">
                    <i class="far fa-clock"></i> {{ formatTime(seatMapLastUpdated) }}
                  </div>
                </div>

                <!-- Legend -->
                <div class="dm-seat-legend" v-if="seatMapData">
                  <span class="leg-item"><span class="leg-dot available"></span>Trống</span>
                  <span class="leg-item"><span class="leg-dot booked"></span>Đã đặt</span>
                  <span class="leg-item"><span class="leg-dot held"></span>Giữ chỗ</span>
                  <span class="leg-item"><span class="leg-dot locked"></span>Khóa</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Ẩn loại vé confirm -->
        <div class="modal" v-if="showDeleteTicketTypeConfirm" @click.self="showDeleteTicketTypeConfirm = false">
          <div class="modal-content" style="max-width:460px">
            <div class="modal-header delete-header">
              <h2><i class="fas fa-eye-slash"></i> Ẩn loại vé thay vì xóa</h2>
              <button class="close-btn" @click="showDeleteTicketTypeConfirm = false">×</button>
            </div>
            <div class="modal-body">
              <div class="delete-message">
                <p>Thay vì xóa loại vé <strong>"{{ ticketTypeToDelete ? ticketTypeToDelete.type : '' }}"</strong>, bạn có thể ẩn nó để khách hàng không thể đặt vé này nữa.</p>
                <p class="warning"><i class="fas fa-info-circle"></i> Việc ẩn an toàn hơn xóa và không ảnh hưởng dữ liệu cũ.</p>
              </div>
            </div>
            <div class="modal-footer">
              <button class="cancel-btn" @click="showDeleteTicketTypeConfirm = false">Hủy</button>
              <button class="hide-btn" @click="hideTicketTypeInstead" :disabled="isDeletingTicket">
                <i class="fas fa-spinner fa-spin" v-if="isDeletingTicket"></i>
                <i class="fas fa-eye-slash" v-else></i> Ẩn loại vé
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Trip Modal -->
    <div class="modal" v-if="showModal" @click.self="closeModal">
      <div class="modal-content trip-modal">
        <div class="modal-header">
          <h2>
            <i :class="isEditing ? 'fas fa-edit' : 'fas fa-plus'"></i>
            {{ isEditing ? 'Chỉnh sửa chuyến đi' : 'Thêm chuyến đi mới' }}
          </h2>
          <button class="close-btn" @click="closeModal">×</button>
        </div>

        <!-- Tab Navigation -->
        <div class="modal-tabs">
          <div
            class="modal-tab"
            :class="{ active: activeTab === 'trip' }"
            @click="activeTab = 'trip'"
          >
            <i class="fas fa-bus"></i> Thông tin chuyến
          </div>
          <div
            class="modal-tab"
            :class="{ active: activeTab === 'tickets' }"
            @click="activeTab = 'tickets'"
          >
            <i class="fas fa-ticket-alt"></i> Loại vé
            <span
              class="tab-badge"
              v-if="
                tripForm &&
                tripForm.ticketTypes &&
                tripForm.ticketTypes.length > 0
              "
            >
              {{ tripForm.ticketTypes.length }}
            </span>
          </div>
          <div
            class="modal-tab"
            :class="{ active: activeTab === 'locations' }"
            @click="activeTab = 'locations'"
          >
            <i class="fas fa-map-marker-alt"></i> Điểm đón
            <span
              class="tab-badge"
              v-if="
                tripForm &&
                tripForm.pickupLocations &&
                tripForm.pickupLocations.length > 0
              "
            >
              {{ tripForm.pickupLocations.length }}
            </span>
          </div>
        </div>

        <div class="modal-body">
          <!-- Tab 1: Trip Info -->
          <div v-show="activeTab === 'trip'" class="tab-content">
            <!-- Các trường thông tin chuyến hiện có -->
            <div class="form-group">
              <label for="trip-name">
                <i class="fas fa-tag"></i> Tên chuyến:
              </label>
              <input
                id="trip-name"
                v-model="tripForm.name"
                type="text"
                class="form-control"
                placeholder="Nhập tên chuyến đi (tùy chọn)"
              />
            </div>
            <div class="form-group">
              <label for="trip-route">
                <i class="fas fa-route"></i> Tuyến đường:
              </label>
              <!-- Search Input -->
              <div class="route-search-wrapper">
                <i class="fas fa-search search-icon"></i>
                <input
                  type="text"
                  v-model="routeSearchQuery"
                  class="route-search-input"
                  placeholder="Tìm kiếm tuyến đường..."
                />
                <button
                  v-if="routeSearchQuery"
                  @click="routeSearchQuery = ''"
                  class="clear-search-btn"
                  type="button"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <!-- Route Dropdown -->
              <select
                id="trip-route"
                v-model="tripForm.route_id"
                required
                class="form-control"
              >
                <option value="" disabled>
                  {{
                    filteredRoutes.length === 0 && routeSearchQuery
                      ? 'Không tìm thấy tuyến đường'
                      : 'Chọn tuyến đường'
                  }}
                </option>
                <option
                  v-for="route in filteredRoutes"
                  :key="route.route_id"
                  :value="route.route_id"
                >
                  {{ route.destination_location }}
                </option>
              </select>
              <small
                v-if="!tripForm.route_id && formErrors.route_id"
                class="error-text"
              >
                {{ formErrors.route_id }}
              </small>
            </div>
            <div class="form-group">
              <label for="trip-vehicle">
                <i class="fas fa-bus"></i> Phương tiện:
              </label>
              <select
                id="trip-vehicle"
                v-model="tripForm.vehicle_id"
                required
                class="form-control"
              >
                <option value="" disabled>Chọn phương tiện</option>
                <option
                  v-for="vehicle in vehicles"
                  :key="vehicle.vehicle_id"
                  :value="vehicle.vehicle_id"
                >
                  {{ formatVehicleType(vehicle.type) }} -
                  {{ vehicle.license_plate }} ({{ vehicle.driver_name }})
                </option>
              </select>
              <small
                v-if="!tripForm.vehicle_id && formErrors.vehicle_id"
                class="error-text"
              >
                {{ formErrors.vehicle_id }}
              </small>
            </div>
            <div class="form-group">
              <label for="trip-datetime">
                <i class="fas fa-clock"></i> Thời gian khởi hành:
              </label>
              <input
                id="trip-datetime"
                type="datetime-local"
                v-model="tripForm.departure_datetime"
                required
                class="form-control"
              />
              <small
                v-if="
                  !tripForm.departure_datetime && formErrors.departure_datetime
                "
                class="error-text"
              >
                {{ formErrors.departure_datetime }}
              </small>
            </div>
            <div class="form-group">
              <label for="trip-duration">
                <i class="fas fa-hourglass-half"></i> Thời gian dự kiến (giờ):
              </label>
              <input
                id="trip-duration"
                type="number"
                v-model="tripForm.estimated_duration"
                required
                class="form-control"
                min="0.5"
                step="0.5"
                placeholder="2.0"
              />
              <small class="help-text"
                >Thời gian dự kiến cho chuyến đi (tính bằng giờ)</small
              >
            </div>
            <div class="form-group">
              <label for="trip-zalo-link">
                <i class="fas fa-users"></i> Link nhóm Zalo:
              </label>
              <input
                id="trip-zalo-link"
                v-model="tripForm.zalo_group_link"
                type="text"
                class="form-control"
                placeholder="Nhập link nhóm Zalo (nếu có)"
              />
              <small class="help-text"
                >Link nhóm Zalo cho hành khách tham gia sau khi đặt vé thành
                công</small
              >
            </div>
            <div class="form-group" v-if="isEditing">
              <label for="trip-status">
                <i class="fas fa-tasks"></i> Trạng thái:
              </label>
              <select
                id="trip-status"
                v-model="tripForm.status"
                required
                class="form-control"
              >
                <option value="scheduled">Đã lên lịch</option>
                <option value="in_progress">Đang chạy</option>
                <option value="completed">Đã hoàn thành</option>
                <option value="canceled">Đã hủy</option>
              </select>
            </div>

            <div class="form-action">
              <button
                class="next-tab-btn"
                :disabled="!isBasicInfoValid()"
                @click="activeTab = 'tickets'"
              >
                Tiếp theo: Loại vé <i class="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>

          <!-- Tab 2: Ticket Types -->
          <div v-show="activeTab === 'tickets'" class="tab-content">
            <div class="tickets-section">
              <h3>Loại vé cho chuyến đi</h3>

              <!-- Ticket Types List -->
              <div
                class="ticket-list"
                v-if="
                  tripForm &&
                  tripForm.ticketTypes &&
                  tripForm.ticketTypes.length > 0
                "
              >
                <div
                  v-for="(ticket, index) in tripForm.ticketTypes"
                  :key="index"
                  class="ticket-item"
                >
                  <div class="ticket-info">
                    <div class="ticket-name">{{ ticket.type }}</div>
                    <div class="ticket-price">
                      {{ formatCurrency(ticket.price) }}
                    </div>
                    <div v-if="ticket.independent" class="ticket-badge special">
                      Vé đặc biệt
                    </div>
                    <div v-if="ticket.is_hidden" class="ticket-badge hidden">
                      Đã ẩn
                    </div>
                  </div>
                  <div class="ticket-actions">
                    <button
                      @click="editTemporaryTicket(index)"
                      class="action-btn small edit-btn"
                    >
                      <i class="fas fa-pen"></i>
                    </button>
                    <button
                      @click="removeTemporaryTicket(index)"
                      class="action-btn small delete-btn"
                    >
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Empty State -->
              <div v-else class="empty-state small">
                <i class="fas fa-ticket-alt"></i>
                <p>Chưa có loại vé nào</p>
              </div>

              <!-- Add Ticket Form -->
              <div class="add-form">
                <h4>
                  <i class="fas fa-plus"></i>
                  {{
                    isEditingTempTicket
                      ? 'Chỉnh sửa loại vé'
                      : 'Thêm loại vé mới'
                  }}
                </h4>

                <div class="form-group">
                  <label for="temp-ticket-type">Tên loại vé:</label>
                  <input
                    id="temp-ticket-type"
                    type="text"
                    v-model="tempTicket.type"
                    class="form-control"
                    placeholder="VD: Người lớn, Trẻ em..."
                  />
                </div>

                <div class="form-group">
                  <label for="temp-ticket-price">Giá vé:</label>
                  <div class="price-input-wrapper">
                    <input
                      id="temp-ticket-price"
                      type="number"
                      v-model="tempTicket.price"
                      class="form-control"
                      placeholder="Nhập giá vé"
                      min="0"
                      step="1000"
                    />
                    <span class="price-suffix">VNĐ</span>
                  </div>
                </div>

                <div class="checkbox-container">
                  <input
                    id="temp-ticket-independent"
                    type="checkbox"
                    v-model="tempTicket.independent"
                    class="form-checkbox"
                  />
                  <label for="temp-ticket-independent" class="checkbox-label">
                    Vé đặc biệt (không cần chọn ghế)
                  </label>
                </div>

                <div class="checkbox-container">
                  <input
                    id="ticket-hidden"
                    type="checkbox"
                    v-model="tempTicket.is_hidden"
                    class="form-checkbox"
                  />
                  <span class="checkbox-label"
                    >Ẩn loại vé này (khách hàng không thể thấy và đặt vé
                    này)</span
                  >
                </div>

                <div class="form-actions">
                  <button @click="resetTempTicket()" class="cancel-btn small">
                    <i class="fas fa-times"></i> Hủy
                  </button>
                  <button
                    @click="addOrUpdateTemporaryTicket()"
                    class="save-btn small"
                    :disabled="!isTempTicketValid()"
                  >
                    <i class="fas fa-check"></i>
                    {{ isEditingTempTicket ? 'Cập nhật' : 'Thêm' }}
                  </button>
                </div>
              </div>
            </div>

            <div class="nav-buttons">
              <button class="back-tab-btn" @click="activeTab = 'trip'">
                <i class="fas fa-arrow-left"></i> Quay lại
              </button>
              <button class="next-tab-btn" @click="activeTab = 'locations'">
                Tiếp theo: Điểm đón <i class="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>

          <!-- Tab 3: Pickup Locations -->
          <div v-show="activeTab === 'locations'" class="tab-content">
            <div class="locations-section">
              <h3>Điểm đón khách</h3>

              <!-- Locations List -->
              <div
                class="location-list"
                v-if="
                  tripForm &&
                  tripForm.pickupLocations &&
                  tripForm.pickupLocations.length > 0
                "
              >
                <div
                  v-for="(location, index) in tripForm.pickupLocations"
                  :key="index"
                  class="location-item"
                >
                  <div class="location-info">
                    <div class="location-name">
                      {{ location.location_name }}
                    </div>
                    <div class="location-address">{{ location.address }}</div>
                  </div>
                  <div class="location-actions">
                    <button
                      @click="editTemporaryLocation(index)"
                      class="action-btn small edit-btn"
                    >
                      <i class="fas fa-pen"></i>
                    </button>
                    <button
                      @click="removeTemporaryLocation(index)"
                      class="action-btn small delete-btn"
                    >
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Empty State -->
              <div v-else class="empty-state small">
                <i class="fas fa-map-marker-alt"></i>
                <p>Chưa có điểm đón nào</p>
              </div>

              <!-- Add Location Form -->
              <div class="add-form">
                <h4>
                  <i class="fas fa-plus"></i>
                  {{
                    isEditingTempLocation
                      ? 'Chỉnh sửa điểm đón'
                      : 'Thêm điểm đón mới'
                  }}
                </h4>

                <div class="form-group">
                  <label for="temp-location-name">Tên điểm đón:</label>
                  <input
                    id="temp-location-name"
                    type="text"
                    v-model="tempLocation.location_name"
                    class="form-control"
                    placeholder="VD: Bến xe miền Đông, Nhà văn hóa Thanh niên..."
                  />
                </div>

                <div class="form-group">
                  <label for="temp-location-address">Địa chỉ chi tiết:</label>
                  <input
                    id="temp-location-address"
                    type="text"
                    v-model="tempLocation.address"
                    class="form-control"
                    placeholder="Nhập địa chỉ chi tiết của điểm đón"
                  />
                </div>

                <div class="form-actions">
                  <button @click="resetTempLocation()" class="cancel-btn small">
                    <i class="fas fa-times"></i> Hủy
                  </button>
                  <button
                    @click="addOrUpdateTemporaryLocation()"
                    class="save-btn small"
                    :disabled="!isTempLocationValid()"
                  >
                    <i class="fas fa-check"></i>
                    {{ isEditingTempLocation ? 'Cập nhật' : 'Thêm' }}
                  </button>
                </div>
              </div>
            </div>

            <div class="nav-buttons">
              <button class="back-tab-btn" @click="activeTab = 'tickets'">
                <i class="fas fa-arrow-left"></i> Quay lại
              </button>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="cancel-btn" @click="closeModal">Hủy</button>
          <button
            class="save-btn"
            @click="submitTripForm"
            :disabled="isSubmitting || !isFormValid()"
          >
            <i class="fas fa-spinner fa-spin" v-if="isSubmitting"></i>
            <i class="fas fa-save" v-else></i>
            {{ isEditing ? 'Cập nhật' : 'Tạo chuyến đi' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      class="modal"
      v-if="showDeleteConfirm"
      @click.self="showDeleteConfirm = false"
    >
      <div class="modal-content">
        <div class="modal-header delete-header">
          <h2><i class="fas fa-exclamation-triangle"></i> Xác nhận xóa</h2>
          <button class="close-btn" @click="showDeleteConfirm = false">
            ×
          </button>
        </div>
        <div class="modal-body">
          <div class="delete-message">
            <p>
              Bạn có chắc chắn muốn xóa chuyến đi
              <strong>"{{ getTripDestination() }}"</strong>?
            </p>
            <p class="warning">
              <i class="fas fa-exclamation-circle"></i>
              Lưu ý: Hành động này không thể hoàn tác.
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showDeleteConfirm = false">
            Hủy
          </button>
          <button class="delete-btn" @click="deleteTrip" :disabled="isDeleting">
            <i class="fas fa-spinner fa-spin" v-if="isDeleting"></i>
            <i class="fas fa-trash-alt" v-else></i>
            Xóa
          </button>
        </div>
      </div>
    </div>

    <!-- Ticket Type Modal -->
    <div
      class="modal"
      v-if="showTicketTypeModal"
      @click.self="closeTicketTypeModal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h2>
            <i :class="isEditingTicket ? 'fas fa-edit' : 'fas fa-plus'"></i>
            {{ isEditingTicket ? 'Chỉnh sửa loại vé' : 'Thêm loại vé mới' }}
          </h2>
          <button class="close-btn" @click="closeTicketTypeModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="ticket-type">
              <i class="fas fa-ticket-alt"></i> Tên loại vé:
            </label>
            <input
              id="ticket-type"
              type="text"
              v-model="ticketTypeForm.type"
              required
              class="form-control"
              placeholder="VD: Người lớn, Trẻ em, Vé cao cấp..."
            />
          </div>
          <div class="form-group">
            <label for="ticket-independent">
              <i class="fas fa-tag"></i> Loại vé đặc biệt:
            </label>
            <div class="checkbox-container">
              <input
                id="ticket-independent"
                type="checkbox"
                v-model="ticketTypeForm.independent"
                class="form-checkbox"
              />
              <span class="checkbox-label"
                >Đây là loại vé đặc biệt (không cần chọn ghế)</span
              >
            </div>
            <small class="form-text"
              >Chọn tùy chọn này cho các loại vé như Bò Lạc, Tự túc,...</small
            >
          </div>
          <div class="form-group">
            <label for="ticket-description">
              <i class="fas fa-info-circle"></i> Mô tả:
            </label>
            <input
              id="ticket-description"
              type="text"
              v-model="ticketTypeForm.description"
              class="form-control"
              placeholder="VD: Vé người lớn, Vé trẻ em (giảm 30%)..."
            />
          </div>
          <div class="form-group">
            <label for="ticket-price">
              <i class="fas fa-tag"></i> Giá vé:
            </label>
            <div class="price-input-wrapper">
              <input
                id="ticket-price"
                type="number"
                v-model="ticketTypeForm.price"
                required
                class="form-control"
                placeholder="Nhập giá vé"
                min="0"
                step="1000"
              />
              <span class="price-suffix">VNĐ</span>
            </div>
            <small v-if="selectedTrip && selectedTrip.route">
              Giá cơ sở: {{ formatCurrency(selectedTrip.route.price) }}
            </small>
          </div>
          <div class="form-group">
            <label for="ticket-hidden">
              <i class="fas fa-eye-slash"></i> Ẩn loại vé:
            </label>
            <div class="checkbox-container">
              <input
                id="ticket-hidden"
                type="checkbox"
                v-model="ticketTypeForm.is_hidden"
                class="form-checkbox"
              />
              <span class="checkbox-label"
                >Ẩn loại vé này (khách hàng không thể thấy và đặt vé này)</span
              >
            </div>
            <small class="form-text"
              >Chọn tùy chọn này để tạm thời ẩn loại vé mà không cần xóa</small
            >
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeTicketTypeModal">Hủy</button>
          <button
            class="save-btn"
            @click="submitTicketTypeForm"
            :disabled="isSubmittingTicket"
          >
            <i class="fas fa-spinner fa-spin" v-if="isSubmittingTicket"></i>
            <i class="fas fa-save" v-else></i>
            {{ isEditingTicket ? 'Cập nhật' : 'Thêm mới' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Pickup Location Modal -->
    <div
      class="modal"
      v-if="showPickupLocationModal"
      @click.self="closePickupLocationModal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h2>
            <i
              :class="isEditingPickupLocation ? 'fas fa-edit' : 'fas fa-plus'"
            ></i>
            {{
              isEditingPickupLocation
                ? 'Chỉnh sửa điểm đón'
                : 'Thêm điểm đón mới'
            }}
          </h2>
          <button class="close-btn" @click="closePickupLocationModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="location-name">
              <i class="fas fa-map-marker-alt"></i> Tên điểm đón:
            </label>
            <input
              id="location-name"
              type="text"
              v-model="pickupLocationForm.location_name"
              required
              class="form-control"
              placeholder="VD: Bến xe miền Đông, Nhà văn hóa Thanh niên..."
            />
          </div>
          <div class="form-group">
            <label for="location-address">
              <i class="fas fa-map"></i> Địa chỉ chi tiết:
            </label>
            <input
              id="location-address"
              type="text"
              v-model="pickupLocationForm.address"
              class="form-control"
              placeholder="Nhập địa chỉ chi tiết của điểm đón"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closePickupLocationModal">
            Hủy
          </button>
          <button
            class="save-btn"
            @click="submitPickupLocationForm"
            :disabled="isSubmittingLocation"
          >
            <i class="fas fa-spinner fa-spin" v-if="isSubmittingLocation"></i>
            <i class="fas fa-save" v-else></i>
            {{ isEditingPickupLocation ? 'Cập nhật' : 'Thêm mới' }}
          </button>
        </div>
      </div>
    </div>

    <div
      class="modal"
      v-if="showDeletePickupLocationConfirm"
      @click.self="showDeletePickupLocationConfirm = false"
    >
      <div class="modal-content">
        <div class="modal-header delete-header">
          <h2>
            <i class="fas fa-exclamation-triangle"></i> Xác nhận xóa điểm đón
          </h2>
          <button
            class="close-btn"
            @click="showDeletePickupLocationConfirm = false"
          >
            ×
          </button>
        </div>
        <div class="modal-body">
          <div class="delete-message">
            <p>
              Bạn có chắc chắn muốn xóa điểm đón
              <strong>"{{ pickupLocationToDelete?.location_name }}"</strong>?
            </p>
            <p class="warning">
              <i class="fas fa-exclamation-circle"></i>
              Lưu ý: Hành động này không thể hoàn tác và có thể ảnh hưởng đến
              các đặt vé đã chọn điểm đón này.
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <button
            class="cancel-btn"
            @click="showDeletePickupLocationConfirm = false"
          >
            Hủy
          </button>
          <button
            class="delete-btn"
            @click="deletePickupLocation"
            :disabled="isDeletingLocation"
          >
            <i class="fas fa-spinner fa-spin" v-if="isDeletingLocation"></i>
            <i class="fas fa-trash-alt" v-else></i>
            Xóa
          </button>
        </div>
      </div>
    </div>

    <!-- Toast notification -->
    <div class="toast-container" v-if="toast.show">
      <div class="toast" :class="toast.type">
        <div class="toast-icon">
          <i :class="getToastIcon()"></i>
        </div>
        <div class="toast-content">
          <div class="toast-title">{{ toast.title }}</div>
          <div class="toast-message">{{ toast.message }}</div>
        </div>
        <button class="toast-close" @click="closeToast">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from '@/plugins/axios'
import AdminSidebar from '../common/AdminSidebar.vue'
import AdminSeatLayout from './AdminSeatLayout.vue'

const isProd = process.env.VUE_APP_IS_PROD === 'true'
const API_BASE_URL = isProd
  ? process.env.VUE_APP_BACKEND_URL_PROD
  : process.env.VUE_APP_BACKEND_URL_DEV

export default {
  name: 'AdminTrips',
  components: {
    AdminSidebar,
    AdminSeatLayout
  },
  data () {
    return {
      activeTab: 'trip',
      showFilters: false,
      trips: [],
      routes: [],
      vehicles: [],
      routeSearchQuery: '',
      tripTicketTypes: [],
      tripBookings: [],
      tripPickupLocations: [],
      isSidebarOpen: false,
      searchQuery: '',
      selectedTrip: null,
      currentUser: {
        full_name: 'Admin User',
        role: 'Administrator'
      },
      dateRange: {
        from: null,
        to: null
      },
      filterRouteId: '',
      filterVehicleType: '',
      filterStatus: '',
      showModal: false,
      showDeleteConfirm: false,
      isEditing: false,
      tripToDelete: null,
      tripForm: {
        name: '',
        route_id: '',
        vehicle_id: '',
        departure_datetime: '',
        estimated_duration: 2.0,
        status: 'scheduled',
        zalo_group_link: '',
        ticketTypes: [],
        pickupLocations: []
      },
      tempTicket: {
        type: '',
        description: '',
        price: '',
        independent: false,
        is_hidden: false
      },
      isEditingTempTicket: false,
      tempTicketIndex: -1,
      tempLocation: {
        location_name: '',
        address: ''
      },
      isEditingTempLocation: false,
      tempLocationIndex: -1,
      formErrors: {
        route_id: '',
        vehicle_id: '',
        departure_datetime: ''
      },
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      isLoading: true,
      isLoadingDetails: false,
      // Seat map
      seatMapData: null,
      isLoadingSeatMap: false,
      isSeatEditMode: false,
      editingLockedSeats: [],
      isSavingLockedSeats: false,
      seatMapLastUpdated: null,
      isSubmitting: false,
      isDeleting: false,
      routeCount: 0,
      tripCount: 0,
      bookingCount: 0,
      scheduledTripCount: 0,
      toast: {
        show: false,
        title: '',
        message: '',
        type: 'info', // success, error, info, warning
        timeout: null
      },
      showTicketTypeModal: false,
      isEditingTicket: false,
      isSubmittingTicket: false,
      isDeletingTicket: false,
      showDeleteTicketTypeConfirm: false,
      ticketTypeForm: {
        ticket_type_id: null,
        trip_id: null,
        type: '',
        description: '',
        price: '',
        independent: false,
        is_hidden: false
      },

      ticketTypeToDelete: null,
      showPickupLocationModal: false,
      isEditingPickupLocation: false,
      isSubmittingLocation: false,
      pickupLocationForm: {
        pickup_location_id: null,
        trip_id: null,
        location_name: '',
        address: ''
      },
      pickupLocationToEdit: null,
      showDeletePickupLocationConfirm: false,
      pickupLocationToDelete: null,
      isDeletingLocation: false,
      groupColors: [
        '#3182ce', // Blue
        '#38a169', // Green
        '#805ad5', // Purple
        '#dd6b20', // Orange
        '#2c7a7b', // Teal
        '#e53e3e', // Red
        '#4a5568', // Gray
        '#d53f8c', // Pink
        '#d69e2e', // Yellow/Gold
        '#0987a0', // Cyan
        '#744210', // Brown
        '#4338ca', // Indigo
        '#EC4899', // Hot Pink
        '#10B981', // Emerald
        '#F97316', // Bright Orange
        '#8B5CF6', // Violet
        '#14B8A6', // Dark Teal
        '#F59E0B', // Amber
        '#EF4444', // Bright Red
        '#6366F1'  // Indigo Blue
      ],
      discountInfo: {},
      addonOrdersByBookingId: {}
    }
  },
  computed: {
    availableSeats () {
      if (
        !this.selectedTrip ||
        !this.selectedTrip.vehicle ||
        !this.tripBookings
      )
        return []

      const vehicleType = this.selectedTrip.vehicle.type
      const totalSeats = this.getVehicleCapacity(vehicleType)
      let allSeats = []

      if (vehicleType === '16_slots') {
        allSeats = Array.from({ length: totalSeats }, (_, i) =>
          (i + 1).toString()
        )
      } else if (vehicleType === '28_slots') {
        allSeats = Array.from({ length: totalSeats }, (_, i) =>
          (i + 1).toString()
        )
      } else if (vehicleType === '45_slots') {
        allSeats = Array.from({ length: totalSeats }, (_, i) =>
          (i + 1).toString()
        )
      } else if (vehicleType === 'over_night') {
        // For sleeper buses with format like 1A, 1B, 2A, 2B...
        const rows = ['A', 'B']
        rows.forEach(row => {
          for (let i = 1; i <= 23; i++) {
            allSeats.push(`${i}${row}`)
          }
        })
      }

      // Remove booked seats
      const bookedSeats = this.tripBookings
        .filter(booking => booking.slot_number !== 'special')
        .map(booking => booking.slot_number)

      return allSeats.filter(seat => !bookedSeats.includes(seat))
    },
    specialTicketCount () {
      if (!this.tripBookings) return 0
      return this.tripBookings.filter(
        booking => booking.slot_number === 'special'
      ).length
    },
    filteredTrips () {
      return this.trips
    },
    filteredRoutes () {
      if (!this.routeSearchQuery) return this.routes

      const query = this.removeVietnameseAccents(this.routeSearchQuery)
      return this.routes.filter(route => {
        const destination = this.removeVietnameseAccents(route.destination_location)
        return destination.includes(query)
      })
    },
    totalPages () {
      return this.totalItems
        ? Math.ceil(this.totalItems / this.itemsPerPage)
        : 1
    },
    displayedPageNumbers () {
      const totalPages = this.totalPages
      const currentPage = this.currentPage
      const maxPagesToShow = 5

      if (totalPages <= maxPagesToShow) {
        return Array.from({ length: totalPages }, (_, i) => i + 1)
      }

      const pages = []
      let startPage = Math.max(currentPage - Math.floor(maxPagesToShow / 2), 1)
      let endPage = startPage + maxPagesToShow - 1

      if (endPage > totalPages) {
        endPage = totalPages
        startPage = endPage - maxPagesToShow + 1
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i)
      }

      return pages
    },
    activeFilterCount () {
      let count = 0
      if (this.filterRouteId) count++
      if (this.filterVehicleType) count++
      if (this.filterStatus) count++
      if (this.dateRange.from) count++
      if (this.dateRange.to) count++
      return count
    },
    hasGroupBookings () {
      return this.tripBookings.some(booking => booking.booking_group_id)
    },

    bookingGroups () {
      if (!this.tripBookings || this.tripBookings.length === 0) return {}

      const groups = {}
      this.tripBookings.forEach(booking => {
        if (booking.booking_group_id) {
          if (!groups[booking.booking_group_id]) {
            groups[booking.booking_group_id] = []
          }
          groups[booking.booking_group_id].push(booking)
        }
      })

      return groups
    }
  },
  methods: {
    formatDob (birthDate, birthYear, cccd) {
      if (!birthDate && !birthYear && !cccd) return '—'
      
      let year = ''
      if (birthYear !== null && birthYear !== undefined && birthYear !== '') {
        const yrStr = String(birthYear).trim()
        if (yrStr && yrStr !== 'null' && yrStr !== 'undefined' && yrStr !== 'NaN') {
          year = yrStr
        }
      }

      if (!year && cccd) {
        const cccdStr = String(cccd).trim()
        if (cccdStr && cccdStr !== 'null' && cccdStr !== 'undefined' && cccdStr.length >= 6) {
          const centuryDigit = parseInt(cccdStr[3], 10)
          const yearDigits = parseInt(cccdStr.slice(4, 6), 10)
          if (!isNaN(centuryDigit) && !isNaN(yearDigits)) {
            const baseYear = centuryDigit <= 1 ? 1900 : 2000
            year = String(baseYear + yearDigits)
          }
        }
      }

      if (birthDate !== null && birthDate !== undefined && birthDate !== '') {
        const cleanDate = String(birthDate).trim()
        if (cleanDate && cleanDate !== 'null' && cleanDate !== 'undefined') {
          const yyyyMmDdPattern = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/
          const matchYmd = cleanDate.match(yyyyMmDdPattern)
          if (matchYmd) {
            const y = matchYmd[1]
            const m = matchYmd[2].padStart(2, '0')
            const d = matchYmd[3].padStart(2, '0')
            return `${d}/${m}/${y}`
          }
          const ddMmYyyyPattern = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/
          const matchDmy = cleanDate.match(ddMmYyyyPattern)
          if (matchDmy) {
            const d = matchDmy[1].padStart(2, '0')
            const m = matchDmy[2].padStart(2, '0')
            const y = matchDmy[3]
            return `${d}/${m}/${y}`
          }
          const ddMmPattern = /^(\d{1,2})[-/](\d{1,2})$/
          const matchDdMm = cleanDate.match(ddMmPattern)
          if (matchDdMm) {
            const d = matchDdMm[1].padStart(2, '0')
            const m = matchDdMm[2].padStart(2, '0')
            const y = year || '1900'
            return `${d}/${m}/${y}`
          }
          if (/^\d{4}$/.test(cleanDate)) {
            return `01/01/${cleanDate}`
          }
          return cleanDate
        }
      }
      
      if (year) {
        return `01/01/${year}`
      }
      return '—'
    },
    removeVietnameseAccents (str) {
      if (!str) return ''
      return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    },
    isBasicInfoValid () {
      return (
        this.tripForm.route_id &&
        this.tripForm.vehicle_id &&
        this.tripForm.departure_datetime
      )
    },
    isFormValid () {
      return this.isBasicInfoValid()
    },

    // Reset form khi đóng modal
    closeModal () {
      this.showModal = false
      this.activeTab = 'trip'
      this.resetTempTicket()
      this.resetTempLocation()
      this.routeSearchQuery = ''
    },

    // Mở form thêm mới với các tab trống
    openAddModal () {
      this.isEditing = false
      this.tripForm = {
        name: '',
        route_id: '',
        vehicle_id: '',
        departure_datetime: this.formatDateForInput(new Date()),
        estimated_duration: 2.0,
        status: 'scheduled',
        zalo_group_link: '',
        ticketTypes: [],
        pickupLocations: []
      }
      this.activeTab = 'trip'
      this.showModal = true
    },

    // Quản lý loại vé tạm thời
    resetTempTicket () {
      this.tempTicket = {
        type: '',
        description: '',
        price: '',
        independent: false
      }
      this.isEditingTempTicket = false
      this.tempTicketIndex = -1
    },

    isTempTicketValid () {
      return this.tempTicket.type && this.tempTicket.price > 0
    },

    editTemporaryTicket (index) {
      if (!this.tripForm || !this.tripForm.ticketTypes) return

      const ticket = this.tripForm.ticketTypes[index]
      if (!ticket) return

      this.tempTicket = { ...ticket }
      this.isEditingTempTicket = true
      this.tempTicketIndex = index
    },

    removeTemporaryTicket (index) {
      this.tripForm.ticketTypes.splice(index, 1)
    },

    getRegularBookingsCount (trip) {
      // For the selected trip, we can calculate an accurate count
      if (
        this.selectedTrip &&
        this.selectedTrip.trip_id === trip.trip_id &&
        this.tripBookings.length > 0
      ) {
        // Count only non-special tickets that aren't canceled
        const regularSeats = this.tripBookings.filter(
          booking =>
            booking.slot_number !== 'special' && booking.status !== 'canceled'
        ).length
        return regularSeats
      }

      if (trip.bookings_count) {
        const specialCount = this.getEstimatedSpecialTickets(trip)
        console.log(
          `Estimated special tickets for trip ${trip.trip_id}: ${specialCount}`
        )
        return Math.max(0, trip.bookings_count - specialCount)
      }

      return 0
    },

    getEstimatedSpecialTickets (trip) {
      // Case 1: For the currently selected trip, use the actual tripBookings data
      if (
        this.selectedTrip &&
        this.selectedTrip.trip_id === trip.trip_id &&
        this.tripBookings.length > 0
      ) {
        const specialCount = this.tripBookings.filter(
          booking =>
            booking.slot_number === 'special' && booking.status !== 'canceled'
        ).length
        console.log(
          `Selected trip ${trip.trip_id} special tickets: ${specialCount}`
        )
        return specialCount
      }

      // Case 2: For other trips in the list, we need to check if we have the data we need
      if (trip.bookings_count !== undefined) {
        // Check if regular_seats exists, if not we need a fallback
        if (trip.regular_seats !== undefined) {
          const specialCount = Math.max(
            0,
            trip.bookings_count - trip.regular_seats
          )
          console.log(
            `Trip ${trip.trip_id} estimated special tickets: ${specialCount}`
          )
          return specialCount
        } else {
          const capacity = this.getVehicleCapacity(trip.vehicle?.type)
          if (capacity) {
            const specialCount = Math.max(0, trip.bookings_count - capacity)
            console.log(
              `Trip ${trip.trip_id} fallback special count: ${specialCount}`
            )
            return specialCount
          }
        }
      }

      console.log(
        `Trip ${trip.trip_id} can't estimate special tickets, returning 0`
      )
      return 0
    },

    addOrUpdateTemporaryTicket () {
      if (!this.isTempTicketValid()) return

      if (!this.tripForm.ticketTypes) {
        this.tripForm.ticketTypes = []
      }

      const ticketData = { ...this.tempTicket }

      if (this.isEditingTempTicket && this.tempTicketIndex >= 0) {
        // Cập nhật loại vé hiện có
        this.tripForm.ticketTypes.splice(this.tempTicketIndex, 1, ticketData)
      } else {
        // Thêm loại vé mới
        this.tripForm.ticketTypes.push(ticketData)
      }

      this.resetTempTicket()
    },

    // Quản lý điểm đón tạm thời
    resetTempLocation () {
      this.tempLocation = {
        location_name: '',
        address: ''
      }
      this.isEditingTempLocation = false
      this.tempLocationIndex = -1
    },

    isTempLocationValid () {
      return this.tempLocation.location_name
    },

    editTemporaryLocation (index) {
      const location = this.tripForm.pickupLocations[index]
      this.tempLocation = { ...location }
      this.isEditingTempLocation = true
      this.tempLocationIndex = index
    },

    removeTemporaryLocation (index) {
      this.tripForm.pickupLocations.splice(index, 1)
    },

    addOrUpdateTemporaryLocation () {
      if (!this.isTempLocationValid()) return

      if (!this.tripForm.pickupLocations) {
        this.tripForm.pickupLocations = []
      }

      const locationData = { ...this.tempLocation }

      if (this.isEditingTempLocation && this.tempLocationIndex >= 0) {
        this.tripForm.pickupLocations.splice(
          this.tempLocationIndex,
          1,
          locationData
        )
      } else {
        this.tripForm.pickupLocations.push(locationData)
      }

      this.resetTempLocation()
    },

    confirmDeletePickupLocation (location) {
      this.pickupLocationToDelete = location
      this.showDeletePickupLocationConfirm = true
    },

    async deletePickupLocation () {
      try {
        this.isDeletingLocation = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Check for active bookings using this pickup location
        try {
          const checkResponse = await axiosInstance.get(
            `/admin/pickup_locations/${this.pickupLocationToDelete.pickup_location_id}/check_bookings`,
            { headers }
          )

          console.log(
            'Active bookings count for pickup location:',
            checkResponse.data
          )

          // If location has active bookings, block deletion
          if (checkResponse.data.active_bookings_count > 0) {
            this.showToast(
              'error',
              `Không thể xóa điểm đón này vì có ${checkResponse.data.active_bookings_count} khách hàng đã chọn điểm đón này.`
            )
            this.showDeletePickupLocationConfirm = false
            return
          }
        } catch (error) {
          console.error('Error checking bookings for pickup location:', error)
          // Continue with deletion attempt if check fails
        }

        // Proceed with deletion if no active bookings
        await axiosInstance.delete(
          `/admin/pickup_locations/${this.pickupLocationToDelete.pickup_location_id}`,
          { headers }
        )

        // Show success message
        this.showToast('success', 'Xóa điểm đón thành công!')

        // Close confirmation modal
        this.showDeletePickupLocationConfirm = false
        this.pickupLocationToDelete = null

        // Refresh pickup locations
        if (this.selectedTrip) {
          await this.fetchTripDetails(this.selectedTrip.trip_id)
        }
      } catch (error) {
        console.error('Error deleting pickup location:', error)
        if (error.response?.status === 409) {
          this.showToast(
            'error',
            'Không thể xóa điểm đón này vì đã có khách hàng chọn điểm đón này.'
          )
        } else {
          this.showToast(
            'error',
            'Có lỗi xảy ra khi xóa điểm đón. Vui lòng thử lại sau.'
          )
        }
      } finally {
        this.isDeletingLocation = false
      }
    },
    toggleSidebar () {
      this.isSidebarOpen = !this.isSidebarOpen
      localStorage.setItem('sidebarOpen', this.isSidebarOpen)
    },
    formatPickupTime (time) {
      if (!time) return 'Không xác định'
      try {
        return new Date(`1970-01-01T${time}`).toLocaleTimeString('vi-VN', {
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return time
      }
    },
    formatCurrency (amount) {
      if (!amount) return 'N/A'
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(amount)
    },
    async fetchTrips () {
      try {
        this.isLoading = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        let fromDate = null
        let toDate = null

        if (this.dateRange.from !== null && this.dateRange.to !== null) {
          fromDate = new Date(this.dateRange.from)
          fromDate.setHours(0, 0, 0, 0)
          toDate = new Date(this.dateRange.to)
          toDate.setHours(23, 59, 59, 999)
        }

        const response = await axiosInstance.get(`/admin/trips/`, {
          headers,
          params: {
            from_date: fromDate,
            to_date: toDate,
            route_id: this.filterRouteId || undefined,
            vehicle_type: this.filterVehicleType || undefined,
            status: this.filterStatus || undefined,
            page: this.currentPage,
            per_page: this.itemsPerPage,
            search: this.searchQuery || undefined
          }
        })

        this.trips = response.data.items || []
        this.totalItems = response.data.total || 0
        this.routeCount = response.data.route_count || 0
        this.tripCount = response.data.trip_count || 0
        this.bookingCount = response.data.booking_count || 0
        this.scheduledTripCount = response.data.scheduled_trip_count || 0
      } catch (error) {
        console.error('Error fetching trips:', error)
        if (error.response?.status === 401) {
          this.handleLogout()
        }
      } finally {
        this.isLoading = false
      }
    },

    async fetchRoutes () {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Lấy tất cả routes đang active từ hôm nay đến hết năm
        const today = new Date()
        const endOfYear = new Date(today.getFullYear(), 11, 31) // 31 tháng 12

        const response = await axiosInstance.get(`/admin/routes/`, {
          headers,
          params: {
            is_active: true, // Lọc theo trạng thái active
            from_date: today.toISOString().split('T')[0], // Format: YYYY-MM-DD
            to_date: endOfYear.toISOString().split('T')[0], // Format: YYYY-MM-DD
            per_page: 1000 // Lấy tất cả routes
          }
        })

        this.routes = response.data
      } catch (error) {
        console.error('Error fetching routes:', error)
      }
    },

    async fetchVehicles () {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        const response = await axiosInstance.get(`/vehicles/`, {
          headers,
          params: {
            per_page: 1000 // Lấy tất cả vehicles
          }
        })

        this.vehicles = response.data
      } catch (error) {
        console.error('Error fetching vehicles:', error)
      }
    },

    async fetchTripDetails (tripId) {
      try {
        this.isLoadingDetails = true
        // Reset data immediately to avoid stale data from previous trip
        this.tripBookings = []
        this.tripTicketTypes = []
        this.tripPickupLocations = []
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Fetch ticket types for this trip
        const ticketTypesResponse = await axiosInstance.get(
          `/admin/ticket_types/`,
          {
            headers,
            params: { trip_id: tripId, per_page: 1000 }
          }
        )
        this.tripTicketTypes = ticketTypesResponse.data

        // Fetch bookings for this trip
        const bookingsResponse = await axiosInstance.get(`/admin/bookings`, {
          headers,
          params: { trip_id: tripId, per_page: 1000 }
        })
        this.tripBookings = bookingsResponse.data.items || []

        // Filter out canceled bookings
        this.tripBookings = this.tripBookings.filter(
          booking => booking.status !== 'canceled'
        )

        // Sort bookings by group ID and then by seat number
        this.tripBookings.sort((a, b) => {
          // First compare by booking group
          if (a.booking_group_id && b.booking_group_id) {
            if (a.booking_group_id !== b.booking_group_id) {
              return a.booking_group_id.localeCompare(b.booking_group_id)
            }
          } else if (a.booking_group_id) {
            return -1 // Items with booking_group_id come first
          } else if (b.booking_group_id) {
            return 1
          }

          // Then handle special tickets
          if (a.slot_number === 'special') return -1
          if (b.slot_number === 'special') return 1

          // For numeric seats
          if (!isNaN(Number(a.slot_number)) && !isNaN(Number(b.slot_number))) {
            return Number(a.slot_number) - Number(b.slot_number)
          }

          // For alphanumeric seats (like in sleeper buses: 1A, 2B, etc.)
          return a.slot_number.localeCompare(b.slot_number, undefined, {
            numeric: true,
            sensitivity: 'base'
          })
        })

        // Fetch pickup locations for this trip
        const pickupLocationsResponse = await axiosInstance.get(
          `/pickup_locations/`,
          {
            headers,
            params: { trip_id: tripId, per_page: 1000 }
          }
        )
        this.tripPickupLocations = pickupLocationsResponse.data
      } catch (error) {
        console.error('Error fetching trip details:', error)
      } finally {
        this.isLoadingDetails = false
      }
    },

    toggleFilters () {
      this.showFilters = !this.showFilters
    },

    applySearch () {
      this.currentPage = 1
      this.fetchTrips()
    },

    translateTripStatus (status) {
      const statusMap = {
        scheduled: 'Đã lên lịch',
        in_progress: 'Đang chạy',
        completed: 'Đã hoàn thành',
        canceled: 'Đã hủy'
      }

      return statusMap[status.toLowerCase()] || status
    },

    translateBookingStatus (status) {
      const statusMap = {
        pending: 'Chờ xác nhận',
        confirmed: 'Đã xác nhận',
        paid: 'Đã thanh toán',
        paying: 'Đang thanh toán',
        canceled: 'Đã hủy',
        refund: 'Hoàn tiền',
        exchange: 'Đổi vé',
        reserved: 'Bảo lưu'
      }

      return statusMap[status.toLowerCase()] || status
    },

    formatVehicleType (type) {
      const types = {
        '16_slots': 'Xe 16 chỗ',
        '28_slots': 'Xe 28 chỗ',
        '45_slots': 'Xe 45 chỗ',
        over_night: 'Xe giường nằm'
      }

      return types[type] || type
    },

    getVehicleCapacity (type) {
      const capacities = {
        '16_slots': 16,
        '28_slots': 28,
        '45_slots': 45,
        over_night: 44
      }

      return capacities[type] || '?'
    },

    handleLogout () {
      localStorage.removeItem('adminToken')
      localStorage.removeItem('refreshToken')
      this.$router.push('/login')
    },

    formatDateTime (dateTime) {
      if (!dateTime) return '-'
      try {
        return new Date(dateTime).toLocaleDateString('vi-VN', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        console.error('Date formatting error:', error)
        return '-'
      }
    },

    handleDateChange (type, event) {
      this.dateRange[type] = event.target.value
    },

    changePage (newPage) {
      this.currentPage = newPage
      this.fetchTrips()
    },

    async viewTripDetails (trip) {
      this.selectedTrip = trip
      this.seatMapData = null
      this.isSeatEditMode = false
      this.fetchSeatMap()
      await this.fetchTripDetails(trip.trip_id)
    },

    closeDetailsModal () {
      this.selectedTrip = null
      this.tripTicketTypes = []
      this.tripBookings = []
      this.tripPickupLocations = []
    },

    // ─── Seat Map Methods ───────────────────────────────────
    getAllSeats (vehicleType) {
      const counts = { '16_slots': 16, '28_slots': 28, '45_slots': 45 }
      const count = counts[vehicleType] || 28
      return Array.from({ length: count }, (_, i) => String(i + 1))
    },

    getSeatClass (seatId) {
      if (!this.seatMapData) return ''
      const lockedList = this.isSeatEditMode ? this.editingLockedSeats : this.seatMapData.locked_seats
      const origLocked = this.seatMapData.locked_seats

      if (this.isSeatEditMode && this.editingLockedSeats.includes(seatId) && !origLocked.includes(seatId)) {
        return 'seat-editing-lock'
      }
      if (lockedList.includes(seatId)) return 'seat-locked'
      if (this.seatMapData.booked_seats.includes(seatId)) return 'seat-booked'
      if (this.seatMapData.held_seats.includes(seatId)) return 'seat-held'
      return 'seat-available'
    },

    getSeatTitle (seatId) {
      if (!this.seatMapData) return seatId
      if (this.seatMapData.booked_seats.includes(seatId)) return `Ghế ${seatId} - Đã đặt`
      if (this.seatMapData.held_seats.includes(seatId)) return `Ghế ${seatId} - Đang giữ`
      const lockedList = this.isSeatEditMode ? this.editingLockedSeats : this.seatMapData.locked_seats
      if (lockedList.includes(seatId)) return `Ghế ${seatId} - Bị khóa`
      return `Ghế ${seatId} - Trống`
    },

    async fetchSeatMap () {
      if (!this.selectedTrip) return
      this.isLoadingSeatMap = true
      try {
        const response = await axiosInstance.get(
          `/admin/trips/${this.selectedTrip.trip_id}/seat-status`
        )
        this.seatMapData = response.data
        this.editingLockedSeats = [...(response.data.locked_seats || [])]
        this.seatMapLastUpdated = new Date()
      } catch (err) {
        console.error('fetchSeatMap error:', err)
        this.seatMapData = null
      } finally {
        this.isLoadingSeatMap = false
      }
    },

    enterSeatEditMode () {
      if (!this.seatMapData) {
        this.fetchSeatMap().then(() => { this.isSeatEditMode = true })
        return
      }
      this.editingLockedSeats = [...(this.seatMapData.locked_seats || [])]
      this.isSeatEditMode = true
    },

    toggleSeatLock (seatId) {
      if (!this.isSeatEditMode) return
      const seatIdStr = String(seatId)
      if (this.seatMapData) {
        if (this.seatMapData.booked_seats.map(s => String(s)).includes(seatIdStr)) return
        if (this.seatMapData.held_seats.map(s => String(s)).includes(seatIdStr)) return
      }
      const idx = this.editingLockedSeats.map(s => String(s)).indexOf(seatIdStr)
      if (idx >= 0) {
        this.editingLockedSeats.splice(idx, 1)
      } else {
        this.editingLockedSeats.push(seatIdStr)
      }
    },

    async saveLockedSeats () {
      if (!this.selectedTrip) return
      this.isSavingLockedSeats = true
      try {
        await axiosInstance.put(
          `/admin/trips/${this.selectedTrip.trip_id}`,
          { locked_seats: this.editingLockedSeats }
        )
        await this.fetchSeatMap()
        this.isSeatEditMode = false
      } catch (err) {
        console.error('saveLockedSeats error:', err)
        alert('Lỗi khi lưu trạng thái khóa ghế: ' + (err.response?.data?.detail || err.message))
      } finally {
        this.isSavingLockedSeats = false
      }
    },

    cancelSeatEdit () {
      this.editingLockedSeats = [...(this.seatMapData ? this.seatMapData.locked_seats : [])]
      this.isSeatEditMode = false
    },

    formatTime (date) {
      if (!date) return ''
      return date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    },
    // ─── End Seat Map Methods ────────────────────────────────


    openAddModal () {
      this.isEditing = false
      this.tripForm = {
        name: '',
        route_id: '',
        vehicle_id: '',
        departure_datetime: this.formatDateForInput(new Date()),
        estimated_duration: 2.0, // Default 2 hours
        status: 'scheduled',
        zalo_group_link: ''
      }
      this.showModal = true
    },

    async openEditModal (trip) {
      this.isEditing = true

      // Lấy dữ liệu cơ bản của chuyến
      this.tripForm = {
        trip_id: trip.trip_id,
        route_id: trip.route_id,
        vehicle_id: trip.vehicle_id,
        name: trip.name,
        departure_datetime: this.formatDateForInput(
          new Date(trip.departure_datetime)
        ),
        estimated_duration: trip.estimated_duration || 2.0,
        status: trip.status,
        zalo_group_link: trip.zalo_group_link || '',
        ticketTypes: [],
        pickupLocations: []
      }

      try {
        // Lấy danh sách loại vé
        const ticketResponse = await axiosInstance.get(`/admin/ticket_types/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('adminToken')}`
          },
          params: { trip_id: trip.trip_id }
        })
        this.tripForm.ticketTypes = ticketResponse.data || []

        // Lấy danh sách điểm đón
        const locationResponse = await axiosInstance.get(`/pickup_locations/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('adminToken')}`
          },
          params: { trip_id: trip.trip_id }
        })
        this.tripForm.pickupLocations = locationResponse.data || []
      } catch (error) {
        console.error('Error loading trip details:', error)
      }

      this.activeTab = 'trip'
      this.showModal = true
      this.closeDetailsModal()
    },

    closeModal () {
      this.showModal = false
    },

    confirmDelete (trip) {
      this.tripToDelete = trip
      this.showDeleteConfirm = true
    },

    formatDateForInput (date) {
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hours = String(d.getHours()).padStart(2, '0')
      const minutes = String(d.getMinutes()).padStart(2, '0')

      return `${year}-${month}-${day}T${hours}:${minutes}`
    },

    getTripDestination () {
      if (!this.tripToDelete) return ''
      return this.tripToDelete.route.destination_location
    },

    getTicketTypeName (ticketTypeId) {
      const ticket = this.tripTicketTypes.find(
        t => t.ticket_type_id === ticketTypeId
      )
      return ticket ? ticket.type : 'N/A'
    },

    getTicketTypePrice (ticketTypeId) {
      const ticket = this.tripTicketTypes.find(
        t => t.ticket_type_id === ticketTypeId
      )
      return ticket ? ticket.price : 0
    },

    getPickupLocationName (locationId) {
      if (!locationId) return 'Không xác định'
      const location = this.tripPickupLocations.find(
        l => l.pickup_location_id === locationId
      )
      return location
        ? `${location.location_name} (${location.address})`
        : 'Không xác định'
    },

    async submitTripForm () {
      try {
        this.isSubmitting = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // If editing an existing trip, check for active bookings first
        if (this.isEditing) {
          try {
            // Check if trip has active bookings
            const checkResponse = await axiosInstance.get(
              `/admin/trips/${this.tripForm.trip_id}/check_bookings`,
              { headers }
            )

            if (checkResponse.data.active_bookings_count > 0) {
              // Check if making significant changes that could affect passengers
              const significantChanges = [
                this.tripForm.route_id !== this.selectedTrip.route_id,
                this.tripForm.vehicle_id !== this.selectedTrip.vehicle_id,
                this.tripForm.departure_datetime !==
                  this.formatDateForInput(
                    new Date(this.selectedTrip.departure_datetime)
                  )
              ]

              if (significantChanges.some(change => change === true)) {
                const confirmMessage = `Cảnh báo: Có ${checkResponse.data.active_bookings_count} đặt vé cho chuyến này. Thay đổi thông tin chuyến có thể ảnh hưởng đến khách hàng. Bạn có muốn tiếp tục?`

                if (!confirm(confirmMessage)) {
                  this.showToast('info', 'Đã hủy thao tác cập nhật chuyến đi')
                  return
                }
              }
            }
          } catch (error) {
            console.error('Error checking bookings for trip:', error)
            // Continue with update attempt if check fails
          }
        }

        // Tách dữ liệu ra khỏi form
        const {
          ticketTypes = [],
          pickupLocations = [],
          ...tripData
        } = this.tripForm

        // Process data differently based on whether we're creating or editing
        let processedPickupLocations = [...pickupLocations]
        let processedTicketTypes = [...ticketTypes]

        if (!this.isEditing) {
          // For pickup locations, remove trip_id field completely
          processedPickupLocations = pickupLocations.map(location => {
            // Extract only the fields we need
            return {
              location_name: location.location_name,
              address: location.address || ''
            }
          })

          // For ticket types, remove trip_id field completely
          processedTicketTypes = ticketTypes.map(ticket => {
            // Extract only the fields we need
            return {
              type: ticket.type,
              description: ticket.description || '',
              price: ticket.price,
              independent: ticket.independent || false,
              is_hidden: ticket.is_hidden || false
            }
          })
        }

        const completeData = {
          trip: tripData,
          ticket_types: processedTicketTypes,
          pickup_locations: processedPickupLocations
        }

        let response

        if (this.isEditing) {
          // Cập nhật thông tin chuyến đi với tất cả dữ liệu liên quan trong 1 request
          response = await axiosInstance.put(
            `/admin/trips/${tripData.trip_id}/complete`,
            completeData,
            { headers }
          )
        } else {
          // Tạo mới chuyến đi với tất cả dữ liệu liên quan trong 1 request
          response = await axiosInstance.post(
            `/admin/trips/complete`,
            completeData,
            { headers }
          )
        }

        // Rest of the code remains the same...
        this.showToast(
          'success',
          this.isEditing
            ? 'Cập nhật chuyến đi thành công!'
            : 'Tạo chuyến đi mới thành công!'
        )

        // Đóng modal và làm mới dữ liệu
        this.closeModal()
        await this.fetchTrips()
      } catch (error) {
        console.error('Error saving trip:', error)

        // Log the complete error response for debugging
        if (error.response?.data) {
          console.log(
            'Error response data:',
            JSON.stringify(error.response.data)
          )
        }

        // Add more detailed error messages
        if (error.response?.status === 409) {
          this.showToast(
            'error',
            'Không thể thay đổi chuyến đi này vì đã có khách hàng đặt vé.'
          )
        } else if (error.response?.status === 422) {
          const errorDetails = error.response.data.detail
          const errorMessage = Array.isArray(errorDetails)
            ? errorDetails[0]?.msg || 'Validation error'
            : 'Validation error'

          this.showToast('error', `Lỗi xác thực dữ liệu: ${errorMessage}`)
        } else {
          this.showToast(
            'error',
            'Có lỗi xảy ra khi lưu dữ liệu. Vui lòng thử lại sau.'
          )
        }
      } finally {
        this.isSubmitting = false
      }
    },

    async deleteTrip () {
      try {
        this.isDeleting = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        const response = await axiosInstance.delete(
          `/trips/${this.tripToDelete.trip_id}`,
          { headers }
        )

        // Update the trip status in the local list
        const tripIndex = this.trips.findIndex(
          trip => trip.trip_id === this.tripToDelete.trip_id
        )

        if (tripIndex !== -1) {
          this.trips[tripIndex].status = 'canceled'
        }

        // Close modal and show success message
        this.showDeleteConfirm = false
        this.tripToDelete = null
        this.showToast('success', 'Đã hủy chuyến đi thành công!')

        // Refresh the data
        await this.fetchTrips()
      } catch (error) {
        console.error('Error canceling trip:', error)
        if (error.response?.status === 409) {
          this.showToast(
            'error',
            'Không thể hủy chuyến đi này vì đang có hành khách đặt chỗ.'
          )
        } else {
          this.showToast(
            'error',
            'Có lỗi xảy ra khi hủy chuyến đi. Vui lòng thử lại sau.'
          )
        }
      } finally {
        this.isDeleting = false
      }
    },

    showToast (type, message) {
      this.toast.show = true
      this.toast.type = type
      this.toast.message = message

      clearTimeout(this.toast.timeout)
      this.toast.timeout = setTimeout(() => {
        this.toast.show = false
      }, 3000)
    },

    closeToast () {
      this.toast.show = false
      clearTimeout(this.toast.timeout)
    },

    resetFilters () {
      this.filterRouteId = ''
      this.filterVehicleType = ''
      this.filterStatus = ''
      this.dateRange.from = null
      this.dateRange.to = null
      this.applySearch()
    },

    calculateBookingPercentage (trip) {
      const capacity = this.getVehicleCapacity(trip.vehicle?.type)
      const bookings = trip.bookings_count || 0
      return (bookings / capacity) * 100
    },

    getBookingFillClass (trip) {
      const percentage = this.calculateBookingPercentage(trip)
      if (percentage >= 75) return 'high'
      if (percentage >= 50) return 'medium'
      return 'low'
    },

    hasBookings (trip) {
      return trip.bookings_count > 0
    },

    getToastIcon () {
      switch (this.toast.type) {
        case 'success':
          return 'fas fa-check-circle'
        case 'error':
          return 'fas fa-exclamation-circle'
        case 'warning':
          return 'fas fa-exclamation-triangle'
        default:
          return 'fas fa-info-circle'
      }
    },
    openAddPickupLocationModal () {
      if (!this.selectedTrip) return

      this.isEditingPickupLocation = false
      this.pickupLocationForm = {
        trip_id: this.selectedTrip.trip_id,
        location_name: '',
        address: ''
      }
      this.showPickupLocationModal = true
    },

    openEditPickupLocationModal (location) {
      this.isEditingPickupLocation = true
      this.pickupLocationForm = {
        pickup_location_id: location.pickup_location_id,
        trip_id: location.trip_id,
        location_name: location.location_name,
        address: location.address || ''
      }
      this.pickupLocationToEdit = location
      this.showPickupLocationModal = true
    },

    closePickupLocationModal () {
      this.showPickupLocationModal = false
      this.pickupLocationForm = {
        pickup_location_id: null,
        trip_id: null,
        location_name: '',
        address: ''
      }
    },

    async submitPickupLocationForm () {
      try {
        this.isSubmittingLocation = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        if (this.isEditingPickupLocation) {
          try {
            // Check if location has active bookings before updating
            const checkResponse = await axiosInstance.get(
              `/admin/pickup_locations/${this.pickupLocationForm.pickup_location_id}/check_bookings`,
              { headers }
            )

            // If there are active bookings, show warning and ask for confirmation
            if (checkResponse.data.active_bookings_count > 0) {
              // Determine if changes are significant
              const isSignificantChange =
                this.pickupLocationForm.location_name !==
                  this.pickupLocationToEdit.location_name ||
                this.pickupLocationForm.address !==
                  this.pickupLocationToEdit.address

              if (isSignificantChange) {
                const confirmMessage = `Cảnh báo: Có ${checkResponse.data.active_bookings_count} đặt vé đã chọn điểm đón này. Thay đổi thông tin có thể ảnh hưởng đến khách hàng. Bạn có muốn tiếp tục?`

                if (!confirm(confirmMessage)) {
                  this.showToast('info', 'Đã hủy thao tác cập nhật điểm đón')
                  return
                }
              }
            }
          } catch (error) {
            console.error('Error checking bookings for pickup location:', error)
          }

          // Proceed with update
          await axiosInstance.put(
            `/admin/pickup_locations/${this.pickupLocationForm.pickup_location_id}`,
            this.pickupLocationForm,
            { headers }
          )
        } else {
          // Create new pickup location
          await axiosInstance.post(
            `/admin/pickup_locations/`,
            this.pickupLocationForm,
            { headers }
          )
        }

        // Show success message
        this.showToast(
          'success',
          this.isEditingPickupLocation
            ? 'Cập nhật điểm đón thành công!'
            : 'Thêm điểm đón mới thành công!'
        )

        // Close modal
        this.closePickupLocationModal()

        // Refresh pickup locations
        if (this.selectedTrip) {
          await this.fetchTripDetails(this.selectedTrip.trip_id)
        }
      } catch (error) {
        console.error('Error saving pickup location:', error)

        // Handle specific error cases
        if (error.response?.status === 409) {
          this.showToast(
            'error',
            'Không thể thay đổi điểm đón này vì đã có khách hàng chọn điểm đón này.'
          )
        } else {
          this.showToast(
            'error',
            'Có lỗi xảy ra khi lưu điểm đón. Vui lòng thử lại sau.'
          )
        }
      } finally {
        this.isSubmittingLocation = false
      }
    },
    openAddTicketTypeModal () {
      if (!this.selectedTrip) return

      this.isEditingTicket = false
      this.ticketTypeForm = {
        trip_id: this.selectedTrip.trip_id,
        type: '',
        description: '',
        price: this.selectedTrip.route?.price || 0,
        independent: false,
        is_hidden: false
      }
      this.showTicketTypeModal = true
    },

    openEditTicketTypeModal (ticket) {
      this.isEditingTicket = true
      this.ticketTypeForm = {
        ticket_type_id: ticket.ticket_type_id,
        trip_id: ticket.trip_id,
        type: ticket.type,
        description: ticket.description || '',
        price: ticket.price,
        independent: ticket.independent,
        is_hidden: ticket.is_hidden
      }
      this.showTicketTypeModal = true
    },

    closeTicketTypeModal () {
      this.showTicketTypeModal = false
      this.ticketTypeForm = {
        ticket_type_id: null,
        trip_id: null,
        type: '',
        description: '',
        price: ''
      }
    },

    confirmDeleteTicketType (ticket) {
      this.ticketTypeToDelete = ticket
      this.showDeleteTicketTypeConfirm = true
    },
    async unhideTicketType (ticket) {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Update ticket type to set is_hidden = false
        await axiosInstance.put(
          `/admin/ticket_types/${ticket.ticket_type_id}`,
          {
            ...ticket,
            is_hidden: false
          },
          { headers }
        )

        // Show success message
        this.showToast('success', 'Đã hiện lại loại vé thành công!')

        // Refresh ticket types
        if (this.selectedTrip) {
          await this.fetchTripDetails(this.selectedTrip.trip_id)
        }
      } catch (error) {
        console.error('Error unhiding ticket type:', error)
        this.showToast(
          'error',
          'Có lỗi xảy ra khi hiện lại loại vé. Vui lòng thử lại sau.'
        )
      }
    },

    async submitTicketTypeForm () {
      try {
        this.isSubmittingTicket = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // If editing an existing ticket type, check for active bookings first
        if (this.isEditingTicket) {
          try {
            // Check if ticket type has active bookings before updating
            const checkResponse = await axiosInstance.get(
              `/admin/ticket_types/${this.ticketTypeForm.ticket_type_id}/check_bookings`,
              { headers }
            )

            // If there are active bookings, show warning and ask for confirmation
            if (checkResponse.data.active_bookings_count > 0) {
              // Determine if changes are significant
              const isSignificantChange =
                this.ticketTypeForm.price !== this.ticketTypeToDelete.price ||
                this.ticketTypeForm.independent !==
                  this.ticketTypeToDelete.independent

              if (isSignificantChange) {
                const confirmMessage = `Cảnh báo: Có ${checkResponse.data.active_bookings_count} đặt vé đang sử dụng loại vé này. Thay đổi thông tin có thể ảnh hưởng đến khách hàng. Bạn có muốn tiếp tục?`

                if (!confirm(confirmMessage)) {
                  this.showToast('info', 'Đã hủy thao tác cập nhật loại vé')
                  return
                }
              }
            }
          } catch (error) {
            console.error('Error checking bookings for ticket type:', error)
          }

          // Proceed with update
          await axiosInstance.put(
            `/admin/ticket_types/${this.ticketTypeForm.ticket_type_id}`,
            this.ticketTypeForm,
            { headers }
          )
        } else {
          // Create new ticket type
          await axiosInstance.post(
            `/admin/ticket_types/`,
            this.ticketTypeForm,
            { headers }
          )
        }

        // Show success message
        this.showToast(
          'success',
          this.isEditingTicket
            ? 'Cập nhật loại vé thành công!'
            : 'Thêm loại vé mới thành công!'
        )

        // Close modal
        this.closeTicketTypeModal()

        // Refresh ticket types
        if (this.selectedTrip) {
          await this.fetchTripDetails(this.selectedTrip.trip_id)
        }
      } catch (error) {
        console.error('Error saving ticket type:', error)

        // Handle specific error cases
        if (error.response?.status === 409) {
          this.showToast(
            'error',
            'Không thể thay đổi hoặc xoá loại vé này vì đã có khách hàng đặt vé. Bạn có thể ẩn loại vé này thay vì thay đổi.'
          )
        } else {
          this.showToast(
            'error',
            'Có lỗi xảy ra khi lưu loại vé. Vui lòng thử lại sau.'
          )
        }
      } finally {
        this.isSubmittingTicket = false
      }
    },

    async hideTicketTypeInstead () {
      try {
        this.isDeletingTicket = true // Reuse the loading state
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Update ticket type to set is_hidden = true
        await axiosInstance.put(
          `/admin/ticket_types/${this.ticketTypeToDelete.ticket_type_id}`,
          {
            ...this.ticketTypeToDelete,
            is_hidden: true
          },
          { headers }
        )

        // Show success message
        this.showToast('success', 'Đã ẩn loại vé thành công!')

        // Close confirmation modal
        this.showDeleteTicketTypeConfirm = false
        this.ticketTypeToDelete = null

        // Refresh ticket types
        if (this.selectedTrip) {
          await this.fetchTripDetails(this.selectedTrip.trip_id)
        }
      } catch (error) {
        console.error('Error hiding ticket type:', error)
        this.showToast(
          'error',
          'Có lỗi xảy ra khi ẩn loại vé. Vui lòng thử lại sau.'
        )
      } finally {
        this.isDeletingTicket = false
      }
    },

    async exportTripToExcel (trip) {
      try {
        // First, fetch complete trip details if needed
        await this.fetchTripDetailsForExport(trip)
        // Debug: log discount code and lookup for each booking
        this.tripBookings.forEach(booking => {
          if (booking.discount_code) {
            const code = booking.discount_code.toUpperCase()
          }
        })

        // Import ExcelJS
        const ExcelJS = await import('exceljs')
        const workbook = new ExcelJS.default.Workbook()

        // Set workbook properties
        workbook.creator = 'DCX Travel'
        workbook.lastModifiedBy = 'DCX Travel'
        workbook.created = new Date()
        workbook.modified = new Date()
        workbook.properties.title = `Báo cáo chuyến đi #${trip.trip_id}`
        workbook.properties.subject = `Thống kê hành khách - ${trip.route?.destination_location}`
        workbook.properties.keywords = 'DCX Travel, Báo cáo, Hành khách'
        workbook.properties.category = 'Báo cáo'

        // ---- 1. TRIP INFO SHEET ----
        // [Keep existing code for the info sheet]
        const infoSheet = workbook.addWorksheet('Thông tin chuyến', {
          properties: { tabColor: { argb: '4472C4' } }
        })

        // Add title row with blue background
        infoSheet.addRow(['THÔNG TIN CHUYẾN ĐI', ''])
        infoSheet.mergeCells('A1:B1')
        const titleCell = infoSheet.getCell('A1')
        titleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFF' } }
        titleCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '1F3864' }
        }
        titleCell.alignment = { horizontal: 'center' }

        // Add spacer row
        infoSheet.addRow(['', ''])

        // Add trip info
        const tripInfos = [
          ['Mã chuyến:', trip.trip_id],
          ['Tên chuyến:', trip.name || trip.route?.destination_location || 'N/A'],
          ['Tuyến đường:', trip.route?.destination_location || 'N/A'],
          [
            'Thời gian khởi hành:',
            this.formatDateTime(trip.departure_datetime)
          ],
          ['Trạng thái:', this.translateTripStatus(trip.status)],
          ['Giá vé cơ bản:', this.formatCurrency(trip.route?.price)],
          ['Tổng số đặt chỗ:', this.getRegularBookingsCount(trip) || 0],
          [
            'Còn trống:',
            this.getVehicleCapacity(trip.vehicle?.type) -
              this.getRegularBookingsCount(trip)
          ]
        ]

        // Add info rows with styling
        tripInfos.forEach(([label, value], index) => {
          const row = infoSheet.addRow([label, value])

          // Style the label cells
          const labelCell = row.getCell(1)
          labelCell.font = { bold: true }
          labelCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'E9EDF5' }
          }
          labelCell.alignment = { horizontal: 'right', vertical: 'middle' }

          // Style specific values
          const valueCell = row.getCell(2)
          if (index === 3) {
            // Status row
            const status = value.toLowerCase()
            let colorArgb = '000000'
            if (status.includes('lên lịch')) colorArgb = '0078D4'
            else if (status.includes('đang chạy')) colorArgb = '004E8C'
            else if (status.includes('hoàn thành')) colorArgb = '217346'
            else if (status.includes('hủy')) colorArgb = 'C42B1C'

            valueCell.font = { bold: true, color: { argb: colorArgb } }
          } else if (index === 4) {
            // Price row
            valueCell.font = { bold: true, color: { argb: '107C41' } }
          }
        })

        // Add vehicle info title with blue background
        infoSheet.addRow(['', ''])
        infoSheet.addRow(['THÔNG TIN PHƯƠNG TIỆN', ''])
        infoSheet.mergeCells('A10:B10')
        const vehicleTitleCell = infoSheet.getCell('A10')
        vehicleTitleCell.font = {
          size: 16,
          bold: true,
          color: { argb: 'FFFFFF' }
        }
        vehicleTitleCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '1F3864' }
        }
        vehicleTitleCell.alignment = { horizontal: 'center' }

        // Add spacer row
        infoSheet.addRow(['', ''])

        // Add vehicle info rows
        const vehicleInfos = [
          ['Loại xe:', this.formatVehicleType(trip.vehicle?.type)],
          ['Biển số xe:', trip.vehicle?.license_plate || 'N/A'],
          ['Tài xế:', trip.vehicle?.driver_name || 'N/A'],
          ['Số điện thoại tài xế:', trip.vehicle?.driver_phone || 'N/A']
        ]

        // Add vehicle info rows with styling
        vehicleInfos.forEach(([label, value]) => {
          const row = infoSheet.addRow([label, value])

          // Style the label cells
          const labelCell = row.getCell(1)
          labelCell.font = { bold: true }
          labelCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'E9EDF5' }
          }
          labelCell.alignment = { horizontal: 'right', vertical: 'middle' }
        })

        // Set column widths for info sheet
        infoSheet.getColumn(1).width = 25
        infoSheet.getColumn(2).width = 50

        // ---- 2. ACTIVE PASSENGERS SHEET ----
        const activeBookings = this.tripBookings.filter(booking =>
          ['pending', 'confirmed', 'paid', 'paying'].includes(
            booking.status.toLowerCase()
          )
        )

        if (activeBookings.length > 0) {
          const activeSheet = workbook.addWorksheet('Hành khách', {
            properties: { tabColor: { argb: '70AD47' } }
          })

          // Define headers with discount information
          const activeHeaders = [
            'Ghế số',
            'Điểm danh',
            'Khách hàng',
            'Giới tính',
            'Số CCCD',
            'Ngày sinh',
            'Địa chỉ CCCD',
            'Số điện thoại',
            'Email',
            'Loại vé',
            'Giá vé',
            'Tiền phụ trợ',
            'Tổng gốc',
            'Giảm giá',
            'Giá cuối',
            'Mã giảm giá',
            'Điểm đón',
            'Trạng thái',
            'Đơn hàng phụ trợ',
            'Ghi chú',
            'Nhóm'
          ]

          // Add header row
          const headerRow = activeSheet.addRow(activeHeaders)

          // Style header row with improved formatting
          headerRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true, color: { argb: 'FFFFFF' }, size: 12 }
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: '1F3864' }
            }
            cell.alignment = {
              horizontal: 'center',
              vertical: 'middle',
              wrapText: true
            }
            cell.border = {
              top: { style: 'thin', color: { argb: 'FFFFFF' } },
              bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
              left: { style: 'thin', color: { argb: 'FFFFFF' } },
              right: { style: 'thin', color: { argb: 'FFFFFF' } }
            }

            // Add specific styling for price columns
            if (colNumber === 11 || colNumber === 12 || colNumber === 13) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '2E5A88' }
              }
            }
          })

          const bookingsByGroup = {}
          activeBookings.forEach(booking => {
            if (booking.booking_group_id) {
              if (!bookingsByGroup[booking.booking_group_id]) {
                bookingsByGroup[booking.booking_group_id] = []
              }
              bookingsByGroup[booking.booking_group_id].push(booking)
            }
          })

          // Group colors for Excel
          const groupStyles = {}
          const groupIds = Object.keys(this.bookingGroups)

          groupIds.forEach((groupId, index) => {
            const colorIndex = index % this.groupColors.length
            const hexColor = this.groupColors[colorIndex].replace('#', '')
            groupStyles[groupId] = hexColor
          })

          const processedGroupIds = new Set()

          const groupRowRanges = {}

          let rowIndex = 2
          activeBookings.forEach((booking, index) => {
            if (booking.booking_group_id) {
              if (!groupRowRanges[booking.booking_group_id]) {
                groupRowRanges[booking.booking_group_id] = {
                  start: rowIndex,
                  end: rowIndex
                }
              } else {
                groupRowRanges[booking.booking_group_id].end = rowIndex
              }
            }

            let addonText = ''

            if (booking.booking_group_id) {
              // For group bookings, only show add-ons for the first row of the group
              if (!processedGroupIds.has(booking.booking_group_id)) {
                // First member of this group - gather all addons for the group
                const groupAddons = []
                const groupMembers = bookingsByGroup[booking.booking_group_id]

                groupMembers.forEach(member => {
                  if (
                    this.addonOrdersByBookingId &&
                    this.addonOrdersByBookingId[member.booking_id]
                  ) {
                    groupAddons.push(
                      ...this.addonOrdersByBookingId[member.booking_id]
                    )
                  }
                })

                // Group identical add-ons and sum quantities
                const addonMap = {}
                groupAddons.forEach(addon => {
                  if (!addonMap[addon.name]) {
                    addonMap[addon.name] = {
                      quantity: 0,
                      price: addon.price
                    }
                  }
                  addonMap[addon.name].quantity += addon.quantity
                })

                // Create summary text
                addonText = Object.keys(addonMap)
                  .map(
                    name =>
                      `${name} x ${
                        addonMap[name].quantity
                      } (${this.formatCurrencyRaw(
                        addonMap[name].price * addonMap[name].quantity
                      )} VND)`
                  )
                  .join('\n')

                // Mark this group as processed
                processedGroupIds.add(booking.booking_group_id)
              }
              // For other members of the group, leave blank - will be merged later
            } else {
              // Individual booking - process normally
              if (
                this.addonOrdersByBookingId &&
                this.addonOrdersByBookingId[booking.booking_id]
              ) {
                const addons = this.addonOrdersByBookingId[booking.booking_id]
                addonText = addons
                  .map(
                    addon =>
                      `${addon.name} x ${
                        addon.quantity
                      } (${this.formatCurrencyRaw(
                        addon.price * addon.quantity
                      )} VND)`
                  )
                  .join('\n')
              }
            }

            if (
              this.addonOrdersByBookingId &&
              this.addonOrdersByBookingId[booking.booking_id]
            ) {
              const addons = this.addonOrdersByBookingId[booking.booking_id]
              addonText = addons
                .map(
                  addon =>
                    `${addon.name} x ${
                      addon.quantity
                    } (${this.formatCurrencyRaw(
                      addon.price * addon.quantity
                    )} VND)`
                )
                .join('\n')
            }

            // Calculate prices separately
            const ticketPrice = this.getTicketTypePrice(booking.ticket_type_id)
            
            // 🔧 Tính tổng giá addon cho booking này
            let addonTotalPrice = 0
            if (this.addonOrdersByBookingId && this.addonOrdersByBookingId[booking.booking_id]) {
              addonTotalPrice = this.addonOrdersByBookingId[booking.booking_id].reduce(
                (sum, addon) => sum + (addon.price * addon.quantity),
                0
              )
            }
            
            // Tổng giá gốc = giá vé + giá addon
            const totalOriginal = ticketPrice + addonTotalPrice
            const discountAmount = this.getCalculatedDiscountAmount(booking)
            const finalPrice = totalOriginal - discountAmount

            // Format DOB: combine birth_date and birth_year smartly
            const dobDisplay = this.formatDob(booking.birth_date || booking.birthDate, booking.birth_year || booking.birthYear, booking.cccd)

            const row = activeSheet.addRow([
              booking.slot_number,
              '', // Empty cell for check-in column
              booking.customer_name,
              booking.gender || '',
              booking.cccd || '',
              dobDisplay,
              booking.cccd_address || '',
              booking.customer_phone,
              booking.customer_email || '',
              this.getTicketTypeName(booking.ticket_type_id),
              ticketPrice,        // 🎫 Giá vé
              addonTotalPrice,    // 🛒️ Tiền phụ trợ
              totalOriginal,      // 💰 Tổng gốc (vé + phụ trợ)
              discountAmount,     // 🎟️ Giảm giá
              finalPrice,         // ✅ Giá cuối
              this.getDiscountDisplayText(booking),
              this.getPickupLocationName(booking.pickup_location_id),
              this.translateBookingStatus(booking.status),
              addonText,
              booking.notes || '',
              booking.booking_group_id
                ? `Nhóm ${groupIds.indexOf(booking.booking_group_id) + 1}`
                : ''
            ])
            rowIndex++

            // Alternating row colors or group colors
            let bgColor = 'FFFFFF'

            if (
              booking.booking_group_id &&
              groupStyles[booking.booking_group_id]
            ) {
              bgColor = groupStyles[booking.booking_group_id]
            } else if (index % 2 === 0) {
              bgColor = 'F2F6FC'
            }

            row.eachCell((cell, colNumber) => {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: bgColor }
              }

              // Add a border for grouped rows
              if (booking.booking_group_id) {
                cell.border = {
                  left: {
                    style: 'thin',
                    color: { argb: this.darkenColorExcel(bgColor, 30) }
                  }
                }
              }

              // Apply number formatting for price columns
              if (colNumber === 11) { // Giá vé
                cell.numFmt = '#,##0 "VNĐ"'
                cell.font = { color: { argb: '1F3864' } }
              } else if (colNumber === 12) { // Tiền phụ trợ
                if (addonTotalPrice > 0) {
                  cell.numFmt = '#,##0 "VNĐ"'
                  cell.font = { color: { argb: '28A745' } }
                } else {
                  cell.value = '-'
                  cell.font = { color: { argb: '6C757D' } }
                }
              } else if (colNumber === 13) { // Tổng gốc
                cell.numFmt = '#,##0 "VNĐ"'
                cell.font = { color: { argb: '000000' }, bold: true }
              } else if (colNumber === 14) { // Giảm giá
                if (discountAmount > 0) {
                  cell.numFmt = '#,##0.00 "VNĐ"'
                  cell.font = { color: { argb: 'DC3545' }, bold: true }
                } else {
                  cell.value = '-'
                  cell.font = { color: { argb: '6C757D' } }
                }
              } else if (colNumber === 15) { // Giá cuối
                cell.numFmt = '#,##0 "VNĐ"'
                cell.font = { color: { argb: '000000' }, bold: true }
              } else if (colNumber === 16) { // Mã giảm giá
                if (booking.discount_code) {
                  cell.font = { color: { argb: '007BFF' }, bold: true }
                }
              }
            })
          })
          
          // 🔧 Calculate totals separately for tickets and addons
          const totalTicketPrice = activeBookings.reduce((total, booking) => {
            return total + this.getTicketTypePrice(booking.ticket_type_id)
          }, 0)
          
          const totalAddonPrice = activeBookings.reduce((total, booking) => {
            if (this.addonOrdersByBookingId && this.addonOrdersByBookingId[booking.booking_id]) {
              return total + this.addonOrdersByBookingId[booking.booking_id].reduce(
                (sum, addon) => sum + (addon.price * addon.quantity),
                0
              )
            }
            return total
          }, 0)
          
          const totalOriginalPrice = totalTicketPrice + totalAddonPrice
          
          const totalDiscountAmount = activeBookings.reduce(
            (total, booking) => total + this.getCalculatedDiscountAmount(booking),
            0
          )
          
          const totalFinalPrice = totalOriginalPrice - totalDiscountAmount

          // Add summary section
          activeSheet.addRow([])
          activeSheet.addRow(['TỔNG KẾT CHUYẾN ĐI'])
          
          const summaryTitleRow = activeSheet.getRow(activeSheet.rowCount)
          summaryTitleRow.eachCell(cell => {
            cell.font = { bold: true, size: 14, color: { argb: 'FFFFFF' } }
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: '1F3864' }
            }
            cell.alignment = { horizontal: 'center' }
          })
          activeSheet.mergeCells(`A${activeSheet.rowCount}:U${activeSheet.rowCount}`) // U vì có 21 cột

          // Add summary details
          const discountBookings = activeBookings.filter(booking => booking.discount_code && Number(booking.discount_amount) > 0)
          const summaryData = [
            ['Tổng số hành khách:', activeBookings.length],
            ['Số khách áp dụng mã giảm giá:', discountBookings.length],
            ['Tổng tiền vé:', totalTicketPrice],
            ['Tổng tiền phụ trợ:', totalAddonPrice],
            ['Tổng giá gốc:', totalOriginalPrice],
            ['Tổng tiền giảm giá:', totalDiscountAmount],
            ['Tổng tiền sau giảm giá:', totalFinalPrice],
            ['Tỷ lệ giảm giá trung bình:', `${totalOriginalPrice > 0 ? ((totalDiscountAmount / totalOriginalPrice) * 100).toFixed(2) : '0.00'}%`]
          ]

          summaryData.forEach(([label, value], index) => {
            const summaryRow = activeSheet.addRow([label, value])
            summaryRow.getCell(1).font = { bold: true }
            summaryRow.getCell(1).fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'F8F9FA' }
            }
            
            // Apply different formatting based on data type
            if (index === 0 || index === 1) {
              // Count fields (số lượng) - no currency format
              summaryRow.getCell(2).numFmt = '#,##0'
              summaryRow.getCell(2).font = { bold: true, color: { argb: '28A745' } }
            } else if (index === 5) {
              // Percentage field - no currency format
              summaryRow.getCell(2).numFmt = '0.00%'
              summaryRow.getCell(2).font = { bold: true, color: { argb: '007BFF' } }
            } else if (typeof value === 'number' && value > 0) {
              // Currency fields
              summaryRow.getCell(2).numFmt = '#,##0 "VNĐ"'
              summaryRow.getCell(2).font = { bold: true, color: { argb: '28A745' } }
            } else if (typeof value === 'number') {
              // Zero currency fields
              summaryRow.getCell(2).numFmt = '#,##0 "VNĐ"'
              summaryRow.getCell(2).font = { bold: true, color: { argb: 'DC3545' } }
            } else {
              summaryRow.getCell(2).font = { bold: true }
            }
          })

          // Add legend for groups
          if (groupIds.length > 0) {
            activeSheet.addRow([])
            activeSheet.addRow(['Chú thích màu nhóm:'])

            groupIds.forEach((groupId, index) => {
              const legendRow = activeSheet.addRow([
                `Nhóm ${index + 1}:`,
                `${this.bookingGroups[groupId].length} khách`
              ])

              legendRow.eachCell((cell, colNumber) => {
                cell.fill = {
                  type: 'pattern',
                  pattern: 'solid',
                  fgColor: { argb: groupStyles[groupId] }
                }
                cell.font = { bold: true }
              })
            })
          }

          // Add total row
          const totalRow = activeSheet.addRow([
            'Tổng',
            '', // Empty cell for check-in column
            `${activeBookings.length} hành khách`,
            '', // Giới tính
            '', // Số CCCD
            '', // Ngày sinh
            '', // Địa chỉ CCCD
            '',
            '',
            '',
            totalTicketPrice,   // Tổng tiền vé
            totalAddonPrice,    // Tổng tiền phụ trợ
            totalOriginalPrice, // Tổng gốc
            totalDiscountAmount,// Tổng giảm giá
            totalFinalPrice,    // Tổng cuối
            '',
            '',
            '',
            '',
            '',
            ''
          ])

          // Style total row
          totalRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true }
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'E9EDF5' }
            }

            if (colNumber === 11) { // Tổng tiền vé
              cell.numFmt = '#,##0 "VNĐ"'
              cell.font = { bold: true, color: { argb: '000000' } }
              cell.alignment = { horizontal: 'right' }
            } else if (colNumber === 12) { // Tổng tiền phụ trợ
              cell.numFmt = '#,##0 "VNĐ"'
              cell.font = { bold: true, color: { argb: '28A745' } }
              cell.alignment = { horizontal: 'right' }
            } else if (colNumber === 13) { // Tổng gốc
              cell.numFmt = '#,##0 "VNĐ"'
              cell.font = { bold: true, color: { argb: '000000' } }
              cell.alignment = { horizontal: 'right' }
            } else if (colNumber === 14) { // Tổng giảm giá
              cell.numFmt = '#,##0.00 "VNĐ"'
              cell.font = { bold: true, color: { argb: 'DC3545' } }
              cell.alignment = { horizontal: 'right' }
            } else if (colNumber === 15) { // Tổng giá sau giảm
              cell.numFmt = '#,##0 "VNĐ"'
              cell.font = { bold: true, color: { argb: '000000' } }
              cell.alignment = { horizontal: 'right' }
            }
          })

          for (const groupId in groupRowRanges) {
            const range = groupRowRanges[groupId]

            // If this group has 2 or more bookings, merge the add-on cells
            if (range.end > range.start) {
              // Get all add-ons for this group
              const groupAddons = []
              const groupBookingIds = bookingsByGroup[groupId].map(
                b => b.booking_id
              )

              groupBookingIds.forEach(bookingId => {
                if (this.addonOrdersByBookingId[bookingId]) {
                  this.addonOrdersByBookingId[bookingId].forEach(addon => {
                    groupAddons.push(addon)
                  })
                }
              })

              // Create a summary text of all add-ons
              let addonSummary = ''
              if (groupAddons.length > 0) {
                // Group identical add-ons and sum quantities
                const addonMap = {}
                groupAddons.forEach(addon => {
                  if (!addonMap[addon.name]) {
                    addonMap[addon.name] = {
                      quantity: 0,
                      price: addon.price
                    }
                  }
                  addonMap[addon.name].quantity += addon.quantity
                })

                addonSummary = Object.keys(addonMap)
                  .map(
                    name =>
                      `${name} x ${
                        addonMap[name].quantity
                      } (${this.formatCurrencyRaw(
                        addonMap[name].price * addonMap[name].quantity
                      )} VND)`
                  )
                  .join('\n')
              }

              // Merge the add-on cells (column S - Đơn hàng phụ trợ)
              activeSheet.mergeCells(`S${range.start}:S${range.end}`)
              const mergedCell = activeSheet.getCell(`S${range.start}`)
              mergedCell.value = addonSummary
              mergedCell.alignment = {
                vertical: 'top',
                wrapText: true
              }
            }
          }

          // Set column widths for all columns
          activeSheet.getColumn(1).width = 8 // Ghế số
          activeSheet.getColumn(2).width = 10 // Điểm danh
          activeSheet.getColumn(3).width = 25 // Khách hàng
          activeSheet.getColumn(4).width = 10 // Giới tính
          activeSheet.getColumn(5).width = 18 // Số CCCD
          activeSheet.getColumn(6).width = 15 // Ngày sinh
          activeSheet.getColumn(7).width = 35 // Địa chỉ CCCD
          activeSheet.getColumn(8).width = 15 // Số điện thoại
          activeSheet.getColumn(9).width = 28 // Email
          activeSheet.getColumn(10).width = 20 // Loại vé
          activeSheet.getColumn(11).width = 15 // Giá vé
          activeSheet.getColumn(12).width = 15 // Tiền phụ trợ
          activeSheet.getColumn(13).width = 15 // Tổng gốc
          activeSheet.getColumn(14).width = 15 // Giảm giá
          activeSheet.getColumn(15).width = 15 // Giá cuối
          activeSheet.getColumn(16).width = 20 // Mã giảm giá
          activeSheet.getColumn(17).width = 40 // Điểm đón
          activeSheet.getColumn(18).width = 15 // Trạng thái
          activeSheet.getColumn(19).width = 30 // Đơn hàng phụ trợ
          activeSheet.getColumn(20).width = 25 // Ghi chú
          activeSheet.getColumn(21).width = 15 // Nhóm

          // Set width for summary section columns
          activeSheet.getColumn('A').width = 35 // Tăng width cho cột A trong summary
          activeSheet.getColumn('B').width = 25 // Cột B trong summary
        }

        // ---- 3. CANCELED BOOKINGS SHEET ----
        // [Also update the canceled bookings sheet if you're including it]
        const canceledBookings = this.tripBookings.filter(
          booking => booking.status.toLowerCase() === 'canceled'
        )

        if (canceledBookings.length > 0) {
          const canceledSheet = workbook.addWorksheet('Đặt chỗ đã hủy', {
            properties: { tabColor: { argb: 'C00000' } }
          })

          // Define headers with new check-in column
          const canceledHeaders = [
            'Ghế số',
            'Khách hàng',
            'Số điện thoại',
            'Email',
            'Loại vé',
            'Giá vé',
            'Trạng thái',
            'Ghi chú',
            'Ngày đặt',
            'Ngày hủy'
          ]

          // Add header row
          const headerRow = canceledSheet.addRow(canceledHeaders)

          // Style header row with improved formatting
          headerRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true, color: { argb: 'FFFFFF' }, size: 12 }
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: '1F3864' }
            }
            cell.alignment = {
              horizontal: 'center',
              vertical: 'middle',
              wrapText: true
            }
            cell.border = {
              top: { style: 'thin', color: { argb: 'FFFFFF' } },
              bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
              left: { style: 'thin', color: { argb: 'FFFFFF' } },
              right: { style: 'thin', color: { argb: 'FFFFFF' } }
            }
          })

          // Add data rows
          canceledBookings.forEach((booking, index) => {
            const row = canceledSheet.addRow([
              booking.slot_number,
              booking.customer_name,
              booking.customer_phone,
              booking.customer_email || '',
              this.getTicketTypeName(booking.ticket_type_id),
              this.formatCurrencyRaw(
                this.getTicketTypePrice(booking.ticket_type_id)
              ),
              this.translateBookingStatus(booking.status),
              booking.notes || '',
              this.formatShortDate(booking.created_at),
              this.formatShortDate(booking.updated_at)
            ])

            // Alternating row colors and styling similar to active bookings
            // [Add similar cell styling as in active bookings]
          })

          // Set column widths
          canceledSheet.getColumn(1).width = 8 // Ghế số
          canceledSheet.getColumn(2).width = 10 // Điểm danh
          canceledSheet.getColumn(3).width = 25 // Khách hàng
          canceledSheet.getColumn(4).width = 15 // Số điện thoại
          canceledSheet.getColumn(5).width = 28 // Email
          canceledSheet.getColumn(6).width = 20 // Loại vé
          canceledSheet.getColumn(7).width = 15 // Giá vé
          canceledSheet.getColumn(8).width = 15 // Trạng thái
          canceledSheet.getColumn(9).width = 25 // Ghi chú
          canceledSheet.getColumn(10).width = 15 // Ngày đặt
          canceledSheet.getColumn(11).width = 15 // Ngày hủy
        }

        // Rest of the code for generating and saving file
        // ...

        // Generate filename
        const dateStr = new Date(trip.departure_datetime)
          .toLocaleDateString('vi-VN')
          .replace(/\//g, '-')
        const fileName = `Chuyen-di-${
          trip.trip_id
        }-${trip.route?.destination_location.replace(
          /\s+/g,
          '-'
        )}-${dateStr}.xlsx`

        // Generate and save file
        const buffer = await workbook.xlsx.writeBuffer()
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })

        // Create download link and trigger click
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = fileName
        link.click()

        this.showToast(
          'success',
          'Xuất Excel thành công',
          'File đã được tải xuống'
        )
      } catch (error) {
        console.error('Error exporting to Excel:', error)
        this.showToast(
          'error',
          'Lỗi xuất Excel',
          'Có lỗi xảy ra khi xuất file Excel. Vui lòng thử lại.'
        )
      }
    },

    formatShortDate (dateString) {
      if (!dateString) return ''
      try {
        return new Date(dateString).toLocaleDateString('vi-VN')
      } catch (error) {
        return dateString
      }
    },

    async fetchTripDetailsForExport (trip) {
      // Always fetch fresh data to ensure we have the latest bookings for export
      this.selectedTrip = trip
      await this.fetchTripDetails(trip.trip_id)

      // Fetch add-on orders for all bookings in this trip
      await this.fetchAddonOrdersForTrip(trip.trip_id)
    },

    async fetchAddonOrdersForTrip (tripId) {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Get all booking IDs from this trip
        const bookingIds = this.tripBookings.map(booking => booking.booking_id)

        if (bookingIds.length === 0) {
          this.tripAddonOrders = []
          return
        }

        // Fetch add-on orders for these bookings
        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/booking-addons/by-trip/${tripId}`,
          {
            headers
          }
        )

        this.tripAddonOrders = response.data || []

        // Build a lookup map for easier access during Excel generation
        this.addonOrdersByBookingId = {}
        this.tripAddonOrders.forEach(order => {
          if (!this.addonOrdersByBookingId[order.booking_id]) {
            this.addonOrdersByBookingId[order.booking_id] = []
          }
          this.addonOrdersByBookingId[order.booking_id].push(order)
        })
      } catch (error) {
        console.error('Error fetching add-on orders:', error)
        this.tripAddonOrders = []
        this.addonOrdersByBookingId = {}
      }
    },


    getDiscountPercentage (booking) {
      if (!booking.discount_code || !booking.discount_amount) {
        return 0
      }

      const ticketPrice = this.getTicketTypePrice(booking.ticket_type_id)
      if (ticketPrice <= 0) return 0

      // Tính phần trăm dựa trên discount_amount từ database
      const percentage = (booking.discount_amount / ticketPrice) * 100
      return Math.round(percentage * 100) / 100 // Round to 2 decimal places
    },

    getDiscountDisplayText(booking) {
      // Hiển thị đúng mã và số tiền giảm cho từng vé từ database
      if (!booking.discount_code || !booking.discount_amount) return ''
      return `${booking.discount_code} (-${this.formatCurrencyRaw(booking.discount_amount)} VND)`
    },


    getFinalPrice (booking) {
      // Giá cuối = giá vé - discount_amount từ database
      const ticketPrice = this.getTicketTypePrice(booking.ticket_type_id)
      if (!booking.discount_amount || booking.discount_amount <= 0) {
        return ticketPrice
      }
      return ticketPrice - booking.discount_amount
    },

    getCalculatedDiscountAmount (booking) {
      // Trả về discount_amount trực tiếp từ database
      return booking.discount_amount || 0
    },

    isDiscountLimited (booking) {
      // Since we don't have access to discount code details anymore,
      // we can't determine if the discount was limited.
      // This method is only used for display purposes in the UI.
      return false
    },

    formatCurrencyRaw (amount) {
      if (!amount) return 0
      return amount // Return raw number for Excel
    },
    getBookingGroupStyle (booking) {
      if (!booking.booking_group_id) return {}

      const groupIds = Object.keys(this.bookingGroups)
      const colorIndex =
        groupIds.indexOf(booking.booking_group_id) % this.groupColors.length
      const backgroundColor = this.groupColors[colorIndex]

      return {
        backgroundColor,
        borderLeft: `4px solid ${this.darkenColor(backgroundColor, 30)}`
      }
    },

    darkenColor (color, percent) {
      // Convert hex to RGB
      let r = parseInt(color.substring(1, 3), 16)
      let g = parseInt(color.substring(3, 5), 16)
      let b = parseInt(color.substring(5, 7), 16)

      // Darken
      r = Math.floor(r * (1 - percent / 100))
      g = Math.floor(g * (1 - percent / 100))
      b = Math.floor(b * (1 - percent / 100))

      // Convert back to hex
      return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
    },
    darkenColorExcel (hexColor, percent) {
      // Convert hex to RGB
      let r = parseInt(hexColor.substring(0, 2), 16)
      let g = parseInt(hexColor.substring(2, 4), 16)
      let b = parseInt(hexColor.substring(4, 6), 16)

      // Darken
      r = Math.floor(r * (1 - percent / 100))
      g = Math.floor(g * (1 - percent / 100))
      b = Math.floor(b * (1 - percent / 100))

      // Convert back to hex
      return ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
    }
  },
  async mounted () {
    const sidebarState = localStorage.getItem('sidebarOpen')
    if (sidebarState !== null) this.isSidebarOpen = sidebarState === 'true'
    await this.fetchRoutes()
    await this.fetchVehicles()
    await this.fetchTrips()
  }
}
</script>

<style scoped>
@import '@/assets/styles/admin.scss';
@import '@/assets/styles/admin-table.scss';

.breadcrumb {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #666;
}

.breadcrumb .current {
  font-weight: bold;
}

.stats-cards {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.stat-card {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 10px 20px;
  width: 23%;
}

.stat-icon {
  font-size: 24px;
  margin-right: 10px;
}

.stat-icon.blue {
  color: #007bff;
}

.stat-icon.green {
  color: #28a745;
}

.stat-icon.purple {
  color: #6f42c1;
}

.stat-icon.orange {
  color: #fd7e14;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 20px;
  font-weight: bold;
}

.stat-label {
  font-size: 14px;
  color: #666;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.loading-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.progress-bar {
  background: #e9ecef;
  border-radius: 4px;
  height: 8px;
  margin-top: 5px;
  width: 100%;
}

.progress {
  border-radius: 4px;
  height: 100%;
}

.progress.low {
  background: #28a745;
}

.progress.medium {
  background: #ffc107;
}

.progress.high {
  background: #dc3545;
}

.page-numbers {
  display: flex;
  align-items: center;
}

.page-number {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  color: #007bff;
  cursor: pointer;
  margin: 0 2px;
  padding: 5px 10px;
}

.page-number.active {
  background: #007bff;
  color: #fff;
}

.toast-container {
  bottom: 20px;
  left: 50%;
  position: fixed;
  transform: translateX(-50%);
  z-index: 9999;
}

.toast {
  align-items: center;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  padding: 10px 20px;
}

.toast-icon {
  font-size: 24px;
  margin-right: 10px;
}

.toast-content {
  display: flex;
  flex-direction: column;
}

.toast-title {
  font-size: 16px;
  font-weight: bold;
}

.toast-message {
  font-size: 14px;
  color: #666;
}

.toast-close {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 16px;
  margin-left: auto;
}

.toast.success {
  border-left: 4px solid #28a745;
}

.toast.error {
  border-left: 4px solid #dc3545;
}

.toast.warning {
  border-left: 4px solid #ffc107;
}

.toast.info {
  border-left: 4px solid #007bff;
}

.delete-header {
  background: #dc3545;
  color: #fff;
}

.delete-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.delete-message .warning {
  color: #dc3545;
  font-weight: bold;
  margin-top: 10px;
}

.pickup-locations-section {
  margin-top: 16px;
}

.location-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
  margin-top: 10px;
}

.location-card {
  display: flex;
  background: #f8f9fa;
  border-left: 4px solid #007bff;
  border-radius: 8px;
  padding: 10px 12px;
  transition: all 0.2s ease;
  align-items: center;
  gap: 8px;
}

.location-card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.location-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #007bff;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-right: 10px;
  flex-shrink: 0;
  font-size: 13px;
}

.location-info {
  flex: 1;
}

.location-name {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 2px;
  color: #333;
}

.location-address {
  color: #666;
  font-size: 12px;
  margin-bottom: 3px;
}

.location-time {
  color: #007bff;
  font-size: 12px;
  font-weight: 500;
}

.location-time i {
  margin-right: 5px;
}

/* ══════════════════════════════════════════
   NEW TRIP DETAIL MODAL (dm-*)
   ══════════════════════════════════════════ */
.details-modal {
  max-width: 1300px !important;
  width: 95vw !important;
  max-height: 92vh !important;
  display: flex !important;
  flex-direction: column !important;
  padding: 0 !important;
  overflow: hidden !important;
}
@media (max-width: 768px) {
  .details-modal { width: 99vw !important; max-height: 99vh !important; }
}

/* Header */
.dm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-bottom: 1px solid #e9ecef;
  background: #fff;
  flex-shrink: 0;
  gap: 10px;
  flex-wrap: wrap;
}
.dm-header-left { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; min-width: 0; }
.dm-trip-id { font-size: 16px; font-weight: 700; color: #2c3e50; }
.dm-trip-name { font-size: 14px; font-weight: 600; color: #495057; }
.dm-route { font-size: 13px; color: #6c757d; }
.dm-status-badge {
  font-size: 11px; font-weight: 600; padding: 2px 8px;
  border-radius: 20px; text-transform: uppercase; letter-spacing: 0.3px;
}
.dm-status-badge.scheduled { background: #e3f8fc; color: #17a2b8; }
.dm-status-badge.in_progress { background: #e8f0fe; color: #1a73e8; }
.dm-status-badge.completed { background: #e6f9ef; color: #28a745; }
.dm-status-badge.canceled { background: #fdecea; color: #dc3545; }
.dm-header-right { display: flex; align-items: center; gap: 8px; }
.dm-edit-btn {
  background: #0066cc; color: #fff; border: none; border-radius: 6px;
  padding: 6px 12px; font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 5px;
}
.dm-edit-btn:hover { background: #0052a3; }
.dm-close-btn {
  background: none; border: none; font-size: 22px; color: #6c757d;
  cursor: pointer; line-height: 1; padding: 0 4px;
}
.dm-close-btn:hover { color: #dc3545; }

/* Body */
.dm-body { flex: 1; overflow-y: auto; display: flex; flex-direction: column; min-height: 0; }
.dm-loading { display: flex; align-items: center; gap: 10px; padding: 30px; justify-content: center; color: #6c757d; }

/* Layout: left scrolls, right is sticky */
.dm-layout {
  display: grid;
  grid-template-columns: 1fr 420px;
  align-items: start;
  flex: 1;
  min-height: 0;
}
@media (max-width: 1100px) {
  .dm-layout { grid-template-columns: 1fr 320px; }
}
@media (max-width: 960px) {
  .dm-layout { grid-template-columns: 1fr; }
  .dm-right { border-left: none !important; border-top: 1px solid #e9ecef; position: static !important; }
}

/* Left column: natural height, scrolls via parent dm-body */
.dm-left { padding: 14px 16px; display: flex; flex-direction: column; gap: 14px; }

/* Meta strip */
.dm-meta-strip {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 8px;
  background: #f8f9fc;
  border-radius: 8px;
  padding: 10px 12px;
  border: 1px solid #e9ecef;
}
.dm-meta-item { display: flex; align-items: flex-start; gap: 8px; }
.dm-meta-item > i { color: #0066cc; font-size: 13px; margin-top: 2px; flex-shrink: 0; }
.dm-meta-label { font-size: 10px; color: #6c757d; text-transform: uppercase; letter-spacing: 0.3px; }
.dm-meta-val { font-size: 13px; font-weight: 500; color: #212529; }
.dm-meta-val.mono { font-family: 'Consolas', monospace; font-size: 12px; letter-spacing: 0.5px; }
.dm-meta-val.blue { color: #0066cc; }
.dm-meta-val.green { color: #28a745; }

/* Section */
.dm-section { background: #fff; border: 1px solid #e9ecef; border-radius: 8px; overflow: hidden; }
.dm-section-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px; background: #f8f9fa; border-bottom: 1px solid #e9ecef;
  font-size: 13px; font-weight: 600; color: #495057;
}
.dm-section-header i { color: #0066cc; margin-right: 5px; }
.dm-add-btn {
  background: #0066cc; color: #fff; border: none; border-radius: 5px;
  padding: 4px 10px; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 4px;
}
.dm-add-btn:hover { background: #0052a3; }

/* Tables */
.dm-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.dm-table thead th {
  background: #f1f3f5; padding: 6px 8px; text-align: left;
  font-weight: 600; color: #495057; border-bottom: 1px solid #dee2e6; white-space: nowrap;
}
.dm-table tbody td { padding: 5px 8px; border-bottom: 1px solid #f0f0f0; vertical-align: top; }
.dm-table tbody tr:last-child td { border-bottom: none; }
.dm-table tbody tr:hover { background: #f8f9fa; }
.dm-table-wrap { overflow-x: auto; }
.dm-passenger-table { min-width: 780px; }

.price-cell { color: #28a745; font-weight: 600; white-space: nowrap; }
.actions-cell { white-space: nowrap; }
.seat-cell { font-weight: 700; text-align: center; color: #2c3e50; }
.small-txt { font-size: 11px; }
.cccd-addr { max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mono { font-family: 'Consolas', monospace; }
.text-muted { color: #adb5bd !important; }

/* Tags */
.dm-tag {
  display: inline-block; font-size: 10px; font-weight: 600;
  padding: 1px 5px; border-radius: 3px; margin-top: 2px;
}
.dm-tag.special { background: #fff3cd; color: #856404; }
.dm-tag.hidden { background: #f8d7da; color: #721c24; }
.dm-tag.group { background: #d1ecf1; color: #0c5460; }
.dm-tag.discount { background: #d4edda; color: #155724; }

/* Gender */
.dm-gender {
  display: inline-block; font-size: 10px; font-weight: 600;
  padding: 1px 5px; border-radius: 3px;
}
.dm-gender.male { background: #cce5ff; color: #004085; }
.dm-gender.female { background: #f8d7da; color: #721c24; }

/* Name */
.dm-name { font-weight: 500; font-size: 12px; }

/* Legend */
.dm-legend { display: flex; align-items: center; gap: 4px; }
.dm-legend-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }

/* Pickup grid */
.dm-pickup-grid { display: flex; flex-direction: column; gap: 0; }
.dm-pickup-card {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 8px 12px; border-bottom: 1px solid #f0f0f0;
}
.dm-pickup-card:last-child { border-bottom: none; }
.dm-pickup-name { font-size: 13px; font-weight: 600; color: #212529; }
.dm-pickup-addr { font-size: 11px; }
.dm-pickup-time { font-size: 11px; color: #0066cc; margin-top: 2px; }
.dm-pickup-actions { display: flex; gap: 4px; flex-shrink: 0; margin-left: 8px; }

/* Icon buttons */
.dm-icon-btn {
  border: none; border-radius: 4px; width: 26px; height: 26px;
  cursor: pointer; font-size: 11px; display: inline-flex; align-items: center; justify-content: center;
}
.dm-icon-btn.edit { background: #e8f0fe; color: #1a73e8; }
.dm-icon-btn.edit:hover { background: #1a73e8; color: #fff; }
.dm-icon-btn.hide { background: #fff3e0; color: #e65100; }
.dm-icon-btn.hide:hover { background: #e65100; color: #fff; }
.dm-icon-btn.show { background: #e6f9ef; color: #2e7d32; }
.dm-icon-btn.show:hover { background: #2e7d32; color: #fff; }
.dm-icon-btn.delete { background: #fdecea; color: #c62828; }
.dm-icon-btn.delete:hover { background: #c62828; color: #fff; }

/* Empty */
.dm-empty { padding: 14px 12px; text-align: center; color: #adb5bd; font-size: 13px; }
.dm-empty i { margin-right: 5px; }

/* RIGHT: Seat Panel — sticky so it stays while left column scrolls */
.dm-right {
  border-left: 1px solid #e9ecef;
  position: sticky;
  top: 0;
  max-height: 92vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
.dm-seat-panel { display: flex; flex-direction: column; }
.dm-seat-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px; border-bottom: 1px solid #e9ecef;
  font-size: 13px; font-weight: 600; color: #495057; position: sticky; top: 0; background: #fff; z-index: 1;
}
.dm-seat-header i { color: #0066cc; margin-right: 5px; }
.dm-seat-btns { display: flex; gap: 5px; }
.dm-sm-btn {
  border: none; border-radius: 5px; padding: 4px 9px; font-size: 12px;
  cursor: pointer; display: flex; align-items: center; gap: 4px;
}
.dm-sm-btn.lock { background: #fff3e0; color: #e65100; }
.dm-sm-btn.lock:hover { background: #e65100; color: #fff; }
.dm-sm-btn.refresh { background: #e8f5e9; color: #2e7d32; }
.dm-sm-btn.refresh:hover { background: #2e7d32; color: #fff; }
.dm-sm-btn.save { background: #0066cc; color: #fff; }
.dm-sm-btn.save:hover { background: #0052a3; }
.dm-sm-btn.cancel { background: #f1f3f5; color: #495057; }
.dm-sm-btn.cancel:hover { background: #dee2e6; }
.dm-sm-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.dm-seat-notice {
  margin: 8px 12px; padding: 4px 10px; border-radius: 6px;
  background: #fff3cd; color: #856404; font-size: 12px;
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
}
.dm-locked-hint {
  display: inline-flex; align-items: center; gap: 3px;
  background: #64748b; color: white; border-radius: 4px;
  padding: 1px 6px; font-size: 11px; font-weight: 600;
}

.dm-seat-body {
  overflow: auto;
  padding: 10px;
}

/* Override overnight-layout: cho phép cuộn ngang nếu 2 tầng không vừa */
.dm-seat-body :deep(.overnight-layout) {
  flex-direction: column;
  gap: 12px;
  align-items: stretch;
}
.dm-seat-body :deep(.overnight-layout .floor-section) {
  flex: unset;
  margin: 0;
  min-width: unset;
}
.dm-seat-body :deep(.seat-map-container) {
  padding: 0.5rem;
  box-shadow: none;
  background: transparent;
}
.dm-seat-body :deep(.desktop-floors) {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.dm-seat-body :deep(.floor-title) {
  font-size: 12px;
  margin-bottom: 6px;
  padding: 3px 8px;
}
.dm-seat-body :deep(.seat-grid) {
  padding: 0.5rem;
  gap: 0.3rem;
}
.dm-seat-body :deep(.seat) {
  min-width: 2rem;
  min-height: 2rem;
  font-size: 0.75rem;
}
/* Ẩn legend trong component vì đã có legend riêng bên dưới */
.dm-seat-body :deep(.seat-legend) {
  display: none;
}

.dm-seat-time { font-size: 11px; color: #adb5bd; margin-top: 6px; text-align: right; }

/* Seat legend */
.dm-seat-legend {
  display: flex; flex-wrap: wrap; gap: 8px; padding: 8px 12px;
  border-top: 1px solid #e9ecef; flex-shrink: 0;
}
.leg-item { display: flex; align-items: center; gap: 4px; font-size: 11px; color: #6c757d; }
.leg-dot { width: 10px; height: 10px; border-radius: 2px; }
.leg-dot.available { background: #e9ecef; border: 1px solid #ced4da; }
.leg-dot.booked { background: #dc3545; }
.leg-dot.held { background: #ffc107; }
.leg-dot.locked { background: #6c757d; }

/* ══ end dm-* ══ */



.info-card {
  display: flex;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 10px 12px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  align-items: center;
}

.info-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.info-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  margin-right: 10px;
  color: white;
  font-size: 13px;
  flex-shrink: 0;
}

.dest-icon {
  background: #0066cc;
}

.time-icon {
  background: #25b0e9;
}

.status-icon {
  background: #6c757d;
}

.status-icon.scheduled {
  background: #17a2b8;
}

.status-icon.in_progress {
  background: #007bff;
}

.status-icon.completed {
  background: #28a745;
}

.status-icon.canceled {
  background: #dc3545;
}

.price-icon {
  background: #25cc7b;
}

.vehicle-icon {
  background: #6f42c1;
}

.license-icon {
  background: #fd7e14;
}

.driver-icon {
  background: #20c997;
}

.phone-icon {
  background: #4f5ec2;
}

.info-details {
  flex: 1;
}

.info-label {
  font-size: 11px;
  color: #6c757d;
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.info-value {
  font-size: 13px;
  font-weight: 500;
  color: #343a40;
}

.status-text {
  font-weight: 600;
}

.status-text.scheduled {
  color: #17a2b8;
}

.status-text.in_progress {
  color: #007bff;
}

.status-text.completed {
  color: #28a745;
}

.status-text.canceled {
  color: #dc3545;
}

.price-value {
  color: #25cc7b;
  font-weight: 600;
}

.license-text {
  font-family: 'Consolas', monospace;
  font-weight: 600;
  letter-spacing: 1px;
}

.phone-text {
  color: #4f5ec2;
  font-weight: 600;
}

.trip-info-section,
.vehicle-info-section {
  margin-bottom: 16px;
}

/* ── 2-column layout inside details modal ── */

.top-details-grid {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 20px;
  align-items: stretch;
}
@media (max-width: 900px) {
  .top-details-grid {
    grid-template-columns: 1fr;
  }
}
.detail-content {
  display: flex !important;
  flex-direction: column !important;
  gap: 20px;
}


.detail-left-col {
  min-width: 0;
}

.detail-right-col {
  position: sticky;
  top: 0;
}

.seat-map-section {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 14px;
  background: #f8f9fa;
}

.seat-map-section .seat-map-container {
  overflow: auto;
  max-height: 480px;
  padding-bottom: 4px;
}

/* Responsive: single column on narrow screens */
@media (max-width: 900px) {
  .detail-content {
    grid-template-columns: 1fr;
  }
  .detail-right-col {
    position: static;
  }
}



.details-section h3 {
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 8px;
  margin-bottom: 12px;
  color: #495057;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.details-section h3 i {
  margin-right: 8px;
  color: #007bff;
}

/* wider modal for 2-col layout */
.details-modal {
  max-width: 1100px !important;
  width: 92vw !important;
}

.details-section {
  margin-bottom: 16px;
}

/* mobile: full-width modal */
@media (max-width: 768px) {
  .details-modal {
    width: 98vw !important;
    max-width: 98vw !important;
  }
}



.action-buttons {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  width: 100%;
  justify-content: center;
}

/* Make search box flexible */
.search-box {
  flex-grow: 1;
  display: flex;
  align-items: center;
}

/* Add responsive styling for mobile screens */
@media screen and (max-width: 768px) {
  .action-buttons {
    flex-direction: row;
    overflow-x: auto; /* Allow horizontal scrolling if needed */
    padding-bottom: 10px; /* Space for scrollbar */
    gap: 8px; /* Reduce gap on mobile */
  }

  .add-btn {
    white-space: nowrap;
    min-width: auto;
    padding-left: 12px;
    padding-right: 12px;
  }

  /* Improve search box on mobile */
  .search-box {
    min-width: 120px;
    flex-basis: 50%;
  }

  .search-box input {
    width: 100%;
    font-size: 0.9rem;
    padding: 8px 30px 8px 30px;
  }
}

.filter-inputs {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.filter-group {
  display: flex;
  flex-direction: column;
}

.filter-group label {
  margin-bottom: 5px;
  display: flex;
  align-items: center;
}

.filter-group label i {
  margin-right: 5px;
}

.filter-select,
.date-input-container {
  width: 100%;
}

.date-input-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter-buttons {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}

.name {
  font-weight: bold;
  color: #147d4a;
}

.destination {
  font-weight: bold;
  word-break: break-word;
  max-width: 150px;
  white-space: normal;
  overflow-wrap: break-word;
  hyphens: auto;
}

/* Also ensure table cells can wrap on smaller screens */
td {
  word-break: break-word;
  white-space: normal;
}

/* Media query for smaller screens to stack by category */
/* Mobile view */
@media screen and (max-width: 768px) {
  .filter-inputs {
    grid-template-columns: 1fr;
  }

  .filter-buttons {
    flex-direction: row;
    margin-top: 15px;
  }

  .filter-buttons button {
    flex: 1;
    margin: 0 5px;
  }
}

table {
  table-layout: fixed;
  width: 100%;
}

td,
th {
  padding: 10px;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  hyphens: auto;
  vertical-align: middle;
}

/* Set specific widths for columns to maintain proportions */
th:nth-child(1),
td:nth-child(1) {
  /* Mã */
  width: 5%;
  min-width: 50px;
}

th:nth-child(2),
td:nth-child(2) {
  /* Mã */
  width: 10%;
  min-width: 50px;
}

th:nth-child(3),
td:nth-child(3) {
  /* Tuyến Đường */
  width: 15%;
  min-width: 150px;
}

th:nth-child(4),
td:nth-child(4) {
  /* Thời gian khởi hành */
  width: 20%;
  min-width: 180px;
}

th:nth-child(5),
td:nth-child(5) {
  /* Xe */
  width: 15%;
  min-width: 120px;
}

th:nth-child(6),
td:nth-child(6) {
  /* Tài xế */
  width: 12%;
  min-width: 100px;
}

th:nth-child(7),
td:nth-child(7) {
  /* Số chỗ đã đặt */
  width: 10%;
  min-width: 100px;
}

th:nth-child(8),
td:nth-child(8) {
  /* Trạng thái */
  width: 10%;
  min-width: 100px;
}

th:nth-child(9),
td:nth-child(9) {
  /* Thao tác */
  width: 13%;
  min-width: 120px;
}

/* Make table responsive with horizontal scroll */
@media screen and (max-width: 1200px) {
  .table-responsive {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  table {
    min-width: 900px;
  }
}

/* Improve action buttons styling to make them uniform */

.action-btn {
  /* display: flex; */
  align-items: center;
  justify-content: center;
  width: 32px; /* Fixed width */
  height: 32px; /* Fixed height */
  border-radius: 4px;
  border: none;
  background-color: #f8f9fa;
  color: #495057;
  transition: all 0.2s ease;
  cursor: pointer;
  flex-shrink: 0; /* Prevents buttons from shrinking */
  padding: 0; /* Remove padding to ensure consistent size */
  margin: 0.5rem;
}

.action-btn:hover {
  transform: translateY(-2px);
}

.action-btn.view-btn:hover {
  background-color: #e3f2fd;
  color: #007bff;
}

.action-btn.edit-btn:hover {
  background-color: #e8f5e9;
  color: #28a745;
}

.action-btn.delete-btn:hover {
  background-color: #ffebee;
  color: #dc3545;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Card action buttons for mobile */
.trip-card-footer {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.card-action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  border-radius: 4px;
  border: none;
  background-color: #f8f9fa;
  color: #495057;
  transition: all 0.2s ease;
  font-size: 13px;
}
.ticket-types-table,
.passenger-list {
  overflow-x: auto;
  margin: 0 -15px; /* Compensate for padding in the modal-body */
  padding: 0 15px;
  position: relative;
}

.ticket-types-table table,
.passenger-list .desktop-only table {
  min-width: 850px; /* Increase minimum width of the table */
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.modal-body {
  padding: 15px;
  overflow-x: hidden; /* Hide horizontal overflow at modal body level */
}

/* Ensure proper spacing around scrollable tables */
.details-section {
  padding: 10px 0;
}

.passenger-list table td {
  padding: 16px;
  background-color: #ffebee;
  color: #dc3545;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Card action buttons for mobile */
.trip-card-footer {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.card-action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  border-radius: 4px;
  border: none;
  background-color: #f8f9fa;
  color: #495057;
  transition: all 0.2s ease;
  font-size: 13px;
}
.ticket-types-table,
.passenger-list {
  overflow-x: auto;
  margin: 0 -15px; /* Compensate for padding in the modal-body */
  padding: 0 15px;
  position: relative;
}

.ticket-types-table table,
.passenger-list .desktop-only table {
  min-width: 850px; /* Increase minimum width of the table */
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.modal-body {
  padding: 15px;
  overflow-x: hidden; /* Hide horizontal overflow at modal body level */
}

/* Ensure proper spacing around scrollable tables */
.details-section {
  padding: 10px 0;
}

.passenger-list table td {
  padding: 16px;
  border-bottom: 1px solid #edf2f7;
  vertical-align: middle;
  word-break: break-word;
  white-space: normal;
}

/* Improve table responsive behavior for ticket types and passenger list */
.ticket-types-table,
.passenger-list {
  overflow-x: auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
  width: 100%;
  position: relative;
}

.passenger-list::-webkit-scrollbar {
  height: 8px;
}

.passenger-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.passenger-list::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 4px;
}

.passenger-list::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

.ticket-types-table table,
.passenger-list .desktop-only table {
  min-width: 800px;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin: 0;
}

/* Ensure all table cells wrap properly */
.ticket-types-table td,
.passenger-list td {
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  hyphens: auto;
  padding: 12px 8px;
  vertical-align: middle;
}

/* Set specific widths for ticket type columns */
.ticket-types-table th:nth-child(1),
.ticket-types-table td:nth-child(1) {
  width: 25%;
  min-width: 120px;
}

.ticket-types-table th:nth-child(2),
.ticket-types-table td:nth-child(2) {
  width: 55%;
  min-width: 200px;
}

.ticket-types-table th:nth-child(3),
.ticket-types-table td:nth-child(3) {
  width: 20%;
  min-width: 100px;
  text-align: right;
}

/* Set specific widths for passenger list columns */
.passenger-list th:nth-child(1),
.passenger-list td:nth-child(1) {
  width: 8%;
  min-width: 70px; /* Increase min-width to fit "Ghế số" */
  white-space: nowrap; /* Prevent this specific text from wrapping */
  text-align: center;
}

.passenger-list th:nth-child(2),
.passenger-list td:nth-child(2) {
  width: 15%;
  min-width: 120px;
}

.passenger-list th:nth-child(3),
.passenger-list td:nth-child(3) {
  width: 17%;
  min-width: 120px;
}

.passenger-list th:nth-child(4),
.passenger-list td:nth-child(4) {
  width: 15%;
  min-width: 100px;
}

.passenger-list th:nth-child(5),
.passenger-list td:nth-child(5) {
  width: 20%;
  min-width: 150px;
}

.passenger-list th:nth-child(6),
.passenger-list td:nth-child(6) {
  width: 15%;
  min-width: 120px;
  text-align: center;
  vertical-align: middle;
}

.passenger-list th:nth-child(7),
.passenger-list td:nth-child(7) {
  width: 10%;
  min-width: 80px;
}

.status-badge.scheduled,
.passenger-list .status-badge.small {
  padding: 4px 6px;
  font-size: 11px;
  min-width: 0;
  max-width: 100%;
  width: fit-content;
  justify-content: center;
  margin: 0 auto;
  display: inline-flex;
  white-space: normal;
  text-align: center;
  line-height: 1.2;
  word-break: break-word;
  flex-wrap: wrap;
}

/* Make sure actions column doesn't overflow */
.passenger-list .booking-actions {
  display: flex;
  justify-content: center;
  gap: 4px;
}

.passenger-list .booking-actions .action-btn {
  margin: 0;
}

.passenger-list tr:hover td {
  background-color: #f8fafc;
}

.passenger-list::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 24px;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 0.7)
  );
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 5;
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
}

.passenger-list:hover::after {
  opacity: 1;
}

/* Sticky header for tables */
.ticket-types-table table th,
.passenger-list table th {
  position: sticky;
  top: 0;
  background-color: #f8fafc;
  z-index: 10;
  padding: 16px 8px;
  text-align: left;
  font-size: 0.85rem;
  font-weight: 600;
  color: #4a5568;
  border-bottom: 2px solid #edf2f7;
  white-space: nowrap; /* Prevent header text from wrapping */
  overflow: visible;
}

.ticket-types-table .price {
  font-weight: 500;
  color: #25cc7b;
  text-align: right;
}

/* Small text in passenger list */
.small-text,
.price-text {
  font-size: 0.85rem;
  color: #6c757d;
  margin-top: 3px;
  display: block;
}

.price-text {
  color: #25cc7b;
  font-weight: 500;
}

/* Ensure modal body has proper overflow handling */
.modal-body {
  padding: 15px;
  overflow-x: hidden;
  max-height: 70vh;
  overflow-y: auto;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  text-transform: capitalize;
  white-space: normal; /* Changed from nowrap to normal */
  transition: all 0.2s ease;
  position: relative;
  max-width: 100%;
}

.status-badge::before {
  content: '';
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

.status-badge.scheduled {
  background-color: rgba(23, 162, 184, 0.15);
  color: #17a2b8;
}

.status-badge.scheduled::before {
  background-color: #17a2b8;
}

.status-badge.in_progress {
  background-color: rgba(0, 123, 255, 0.15);
  color: #007bff;
}

.status-badge.in_progress::before {
  background-color: #007bff;
}

.status-badge.completed {
  background-color: rgba(40, 167, 69, 0.15);
  color: #28a745;
}

.status-badge.completed::before {
  background-color: #28a745;
}

.status-badge.canceled {
  background-color: rgba(220, 53, 69, 0.15);
  color: #dc3545;
}

.status-badge.canceled::before {
  background-color: #dc3545;
}

/* Hover effect for status badges */
.status-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

/* Small variant for status badges */
.status-badge.small {
  padding: 4px 8px;
  font-size: 12px;
}

.status-badge.small::before {
  width: 6px;
  height: 6px;
  margin-right: 4px;
}

/* Status text styling in details modal */
.status-text {
  font-weight: 700;
  display: inline-flex;
  align-items: center;
}

.status-text::before {
  content: '';
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
}

.status-text.scheduled::before {
  background-color: #17a2b8;
}

.status-text.in_progress::before {
  background-color: #007bff;
}

.status-text.completed::before {
  background-color: #28a745;
}

.status-text.canceled::before {
  background-color: #dc3545;
}
.status {
  justify-content: center;
  align-items: center;
  padding: 2px 0;
}

td.status .status-badge {
  display: inline-flex;
  padding: 4px 10px;
  font-size: 12px;
  min-width: 0; /* Remove min-width constraint */
  width: fit-content;
  max-width: 100%;
  justify-content: center;
  white-space: normal;
  word-break: break-word;
}

td.status .status-badge::before {
  width: 6px;
  height: 6px;
  margin-right: 5px;
}

.trip-card-header .status .status-badge {
  padding: 4px 8px;
  font-size: 11px;
  min-width: 90px;
}

/* Group styling */
.group-row {
  position: relative;
}

.group-badge {
  display: inline-block;
  background-color: rgba(79, 70, 229, 0.1);
  color: #4f46e5;
  font-size: 11px;
  padding: 2px 6px;
  margin-top: 4px;
  border-radius: 4px;
  font-weight: 500;
}

.group-badge i {
  font-size: 10px;
}

/* Gender badge */
.gender-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.gender-badge.male {
  background-color: rgba(59, 130, 246, 0.1);
  color: #2563eb;
}

.gender-badge.female {
  background-color: rgba(236, 72, 153, 0.1);
  color: #db2777;
}

/* CCCD & birth info */
.cccd-text {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.cccd-address-text {
  font-size: 12px;
  max-width: 200px;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.text-muted {
  color: #9ca3af;
  font-size: 12px;
}

/* Group legend styling */
.group-legend {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 10px 15px;
  margin-bottom: 15px;
  border: 1px solid #e9ecef;
}

.legend-title {
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 8px;
  color: #495057;
}

.legend-title i {
  margin-right: 6px;
  color: #4f46e5;
}

.legend-items {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 13px;
  background-color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
}

.color-swatch {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  margin-right: 6px;
}

/* Responsive styling for seat grid */
@media screen and (max-width: 768px) {
  .available-seats-grid {
    grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
    gap: 8px;
  }

  .available-seat {
    height: 35px;
    font-size: 13px;
  }

  .legend-items {
    flex-direction: column;
    gap: 5px;
  }
}

@media screen and (max-width: 768px) {
  .status {
    justify-content: flex-start;
  }
}

/* Discount info styling */
.discount-info {
  margin-top: 5px;
}

.discount-badge {
  display: inline-block;
  background-color: #e3f2fd;
  color: #1976d2;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  border: 1px solid #bbdefb;
}

.final-price {
  font-size: 12px;
  color: #28a745;
  font-weight: 600;
  margin-top: 2px;
  display: inline-block;
}

.discount-limit-warning {
  font-size: 10px;
  color: #ffc107;
  font-weight: 500;
  margin-top: 2px;
  display: inline-block;
}

.discount-limit-warning i {
  margin-right: 3px;
}
.price-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.price-suffix {
  position: absolute;
  right: 10px;
  color: #6c757d;
  font-weight: 500;
}

.price-input-wrapper input {
  padding-right: 50px;
}

/* Small action button styling for headers */
.action-btn-sm {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #f8f9fa;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s;
  margin-left: 10px;
  vertical-align: middle;
}

.action-btn-sm:hover {
  background: #e9ecef;
  color: #007bff;
  transform: scale(1.1);
}

.add-btn-sm {
  color: #28a745;
}

.add-btn-sm:hover {
  background: #e8f5e9;
  color: #28a745;
}

/* Update ticket types table column widths */
.ticket-types-table th:nth-child(1),
.ticket-types-table td:nth-child(1) {
  width: 20%;
  min-width: 100px;
}

.ticket-types-table th:nth-child(2),
.ticket-types-table td:nth-child(2) {
  width: 50%;
  min-width: 180px;
}

.ticket-types-table th:nth-child(3),
.ticket-types-table td:nth-child(3) {
  width: 15%;
  min-width: 100px;
  text-align: right;
}

.ticket-types-table th:nth-child(4),
.ticket-types-table td:nth-child(4) {
  width: 15%;
  min-width: 80px;
  text-align: center;
}

.action-btn.excel-btn:hover {
  background-color: #e8f4ea;
  color: #217346; /* Excel green color */
}

.card-action-btn.excel-btn {
  background-color: #f5fbf6;
  color: #217346;
}

.card-action-btn.excel-btn:hover {
  background-color: #e8f4ea;
}

.location-card {
  position: relative;
  padding-right: 40px; /* Make space for action buttons */
}

.action-btn.small {
  width: 28px;
  height: 28px;
  font-size: 0.75rem;
}

.name-icon {
  background: #ff9d00;
}

.trip-name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #444;
  margin-right: auto;
  margin-left: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

/* Update trip-card-header to accommodate the name */
.trip-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 10px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.form-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 10px;
  cursor: pointer;
}

.checkbox-label {
  font-size: 14px;
  cursor: pointer;
}

.form-text {
  font-size: 12px;
  color: #6c757d;
  margin-top: 4px;
}
.trip-modal {
  max-width: 800px;
  width: 90%;
}

.modal-tabs {
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 20px;
  position: relative;
}

.modal-tab {
  padding: 12px 20px;
  cursor: pointer;
  font-weight: 500;
  color: #718096;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-tab:hover {
  color: #4a5568;
  background-color: #f7fafc;
}

.modal-tab.active {
  color: #3182ce;
  border-bottom: 2px solid #3182ce;
}

.tab-badge {
  display: inline-block;
  background-color: #ebf4ff;
  color: #3182ce;
  border-radius: 9999px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
}

.tab-content {
  min-height: 400px;
  display: flex;
  flex-direction: column;
}

.add-form {
  padding: 16px;
  background-color: #f8fafc;
  border-radius: 8px;
  margin-top: 20px;
}

.add-form h4 {
  font-size: 16px;
  font-weight: 600;
  color: #3182ce;
  margin-bottom: 16px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}

.ticket-list,
.location-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.ticket-item,
.location-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background-color: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.ticket-info,
.location-info {
  flex: 1;
}

.ticket-name,
.location-name {
  font-weight: 600;
  font-size: 14px;
}

.ticket-price {
  color: #38a169;
  font-weight: 500;
  margin-top: 4px;
}

.location-address {
  color: #718096;
  font-size: 13px;
  margin-top: 4px;
}

.ticket-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  margin-top: 4px;
}

.ticket-badge.hidden {
  background-color: #cbd5e0;
  color: #4a5568;
}

.ticket-badge.special {
  background-color: #e9d8fd;
  color: #805ad5;
}

.nav-buttons {
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
}

.back-tab-btn,
.next-tab-btn {
  padding: 8px 16px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.back-tab-btn {
  background-color: #f7fafc;
  color: #4a5568;
  border: 1px solid #e2e8f0;
}

.next-tab-btn {
  background-color: #4299e1;
  color: white;
  border: none;
}

.back-tab-btn:hover {
  background-color: #edf2f7;
}

.next-tab-btn:hover {
  background-color: #3182ce;
}

.next-tab-btn:disabled {
  background-color: #a0aec0;
  cursor: not-allowed;
}

/* Responsive styles */
@media screen and (max-width: 768px) {
  .modal-tabs {
    overflow-x: auto;
  }

  .modal-tab {
    padding: 12px;
    font-size: 13px;
  }
}

.hide-btn {
  background-color: #718096;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.hide-btn:hover {
  background-color: #4a5568;
}

.hide-btn:disabled {
  opacity: 0.6;
  cursor: wait;
}
.action-btn.hide-action-btn:hover {
  background-color: #edf2f7;
  color: #718096;
}
.action-btn.unhide-btn:hover {
  background-color: #e6fffa;
  color: #319795;
}

.available-seats-section h3 {
  display: flex;
  align-items: center;
}

.seat-count {
  font-size: 14px;
  color: #6c757d;
  margin-left: 8px;
  font-weight: normal;
}

.available-seats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 10px;
  margin-top: 15px;
}

.available-seat {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e6fffa;
  color: #25cc7b;
  border: 1px solid #b2f5ea;
  border-radius: 6px;
  height: 40px;
  font-weight: 600;
  transition: all 0.2s;
}

.available-seat:hover {
  transform: translateY(-2px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

.special-tickets-note {
  background-color: #f0f4f8;
  color: #4a5568;
  padding: 10px 15px;
  border-radius: 6px;
  margin-top: 10px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.special-tickets-note i {
  color: #3182ce;
}

.seats-container {
  margin-top: 15px;
}

/* Responsive styling for seat grid */
@media screen and (max-width: 768px) {
  .available-seats-grid {
    grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
    gap: 8px;
  }

  .available-seat {
    height: 35px;
    font-size: 13px;
  }
}

/* Route Search Styling */
.route-search-wrapper {
  position: relative;
  margin-bottom: 10px;
}

.route-search-input {
  width: 100%;
  padding: 10px 40px 10px 38px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  transition: all 0.2s;
  font-size: 14px;
}

.route-search-input:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
  outline: none;
}

.route-search-wrapper .search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  pointer-events: none;
}

.clear-search-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.clear-search-btn:hover {
  color: #718096;
}

.clear-search-btn i {
  font-size: 12px;
}

/* ─── Seat Map Styles ───────────────────────────────────── */
.seat-map-section h3 {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.seat-map-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}

.lock-btn-sm {
  background: #fff7e6;
  color: #d97706;
  border: 1px solid #fbbf24;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: background 0.15s;
}
.lock-btn-sm:hover { background: #fde68a; }

.refresh-btn-sm {
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #d1d5db;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s;
}
.refresh-btn-sm:hover { background: #e5e7eb; }
.refresh-btn-sm:disabled { opacity: 0.5; cursor: not-allowed; }

.save-seat-btn {
  background: #059669;
  color: #fff;
  border: none;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.save-seat-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.cancel-seat-btn {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
}

.seat-edit-notice {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #1d4ed8;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 14px;
}

.seat-loading {
  text-align: center;
  padding: 24px;
  color: #6b7280;
  font-size: 14px;
}

.seat-map-container {
  margin-top: 10px;
}

.seat-stats {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  font-size: 13px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.stat-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.seat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(52px, 1fr));
  gap: 8px;
  max-width: 600px;
  margin-bottom: 16px;
}

.seat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 54px;
  border-radius: 8px;
  border: 2px solid transparent;
  cursor: default;
  transition: all 0.15s;
  font-size: 11px;
  font-weight: 600;
  user-select: none;
}

.seat-chair-icon {
  font-size: 18px;
  margin-bottom: 2px;
}

.seat-num {
  font-size: 11px;
  font-weight: 600;
}

/* Seat states */
.seat-available {
  background: #f0fdf4;
  border-color: #86efac;
  color: #166534;
}
.seat-available:hover {
  background: #dcfce7;
}

.seat-booked {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #991b1b;
  cursor: not-allowed;
}

.seat-held {
  background: #fef9c3;
  border-color: #fde047;
  color: #713f12;
  cursor: not-allowed;
}

.seat-locked {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #4b5563;
  cursor: default;
}

.seat-editing-lock {
  background: #fef3c7;
  border-color: #f59e0b;
  color: #92400e;
  cursor: pointer;
  border-style: dashed;
}

/* Seat when in edit mode (clickable) */
.seat-editing-lock:hover,
.seat-available:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 6px rgba(0,0,0,0.12);
}

/* Dot colors for stats and legend */
.available-dot { background: #22c55e; }
.booked-dot { background: #ef4444; }
.held-dot { background: #eab308; }
.locked-dot { background: #9ca3af; }
.editing-dot { background: #f59e0b; border: 2px dashed #d97706; }

/* Overnight seat layout */
.overnight-grid {
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
  overflow-x: auto;
}

.overnight-column {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
}

.overnight-col-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 4px;
}

.overnight-seat {
  width: 48px;
  height: 36px;
  border-radius: 6px;
  font-size: 12px;
}

/* Legend */
.seat-legend {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 12px;
  font-size: 13px;
  color: #374151;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  width: 14px;
  height: 14px;
  border-radius: 3px;
}

.seat-updated-time {
  margin-top: 8px;
  font-size: 11px;
  color: #9ca3af;
  text-align: right;
}
/* ─── End Seat Map Styles ───────────────────────────────── */

</style>
